// ResultScene — the M3 polished match-summary card. Records the board into lifetime stats + the
// session run ledger, then shows: WON/LOST headline, you-vs-AI, accuracy, best combo, a board-by-
// board run strip, and any fresh achievement ("NEW BEST!", "DAILY STREAK +1"). Actions: CONTINUE,
// PLAY AGAIN, CHALLENGE (brag card + deep link), and LEADERBOARD (submit + top-N tabs).
//
// Pure consumer of the final Snapshot + the run ledger; never touches Core/determinism. Stats are
// recorded exactly ONCE per board (guarded) so a re-render can't double-count.
import Phaser from 'phaser';
import { SCORING, type Snapshot } from '../../core';
import { SEED_TOPICS } from '../../data/seedPack';
import { PALETTE, hex, FONT } from '../keys';
import { motionAllowed, scaledFont } from '../settings';
import { typePx } from '../ui/typeScale';
import { REG } from '../registryKeys';
import {
  advanceSeries,
  isSeriesOver,
  seriesGameLabel,
  seriesSeed,
  seriesStandingText,
  seriesWonByYou,
  type SeriesState,
} from '../series';
import { audio } from '../audio';
import { resultSceneBark } from '../rivalPersona';
import { addBackdrop } from '../backdrop';
import { celebrate } from '../juice';
import { icon, drawIcon, type IconName } from '../icons';
import {
  recordBoardOutcome,
  firstLabel,
  getStats,
  liveDailyStreak,
  dailyDoneToday,
  type RecordResult,
} from '../stats';
import { appendRun, getRun, resetRun, runTotals, type RunBoard } from '../run';
import { openLeaderboard } from '../leaderboardPanel';
import { readLadder, markGateBeaten, markRungBeaten, type LadderRace } from '../ghostLadder';
import { readRivalMemory, recordRivalMatch, grudgeResultLine } from '../rivalMemory';
import { track, type AnalyticsDims, type BoardSource } from '../../analytics/client';
import { markOnboardingSeen } from '../onboarding';
import {
  isFinalNoviceBoard,
  noviceAiEnabled,
  noviceBoardFor,
  NOVICE_BOARD_COUNT,
  NOVICE_TOPIC_CATEGORY,
} from '../noviceRun';
import { recordFactLearned, recordTopicCorrect, topicMastery } from '../mastery';
import { attachMascot } from '../mascot';
import { selectedCosmetic, nextCosmeticGoal } from '../cosmetics';
import { ctaButton, secondaryButton, statTileRow, rankRibbon, chip, labelText, progressBar, cardPanel } from '../ui/kit';
import { openShareSheet } from '../shareSheetPanel';
import { shareResult, type ShareCardData } from '../share';
import {
  buildKillcamModel,
  playKillcam,
  killcamWebMSupported,
  shareKillcamVideo,
  type KillcamModel,
} from '../killcam';
import type { GameScene } from './GameScene';
import { PHOTO_FINISH_MARGIN } from '../game/eventFeedback';
import {
  buildFeelReviewDraft,
  resetFeelReviewSession,
} from '../playtestSession';
import * as Report from '../game/resultReport';

type ShareRouteCells = NonNullable<ShareCardData['cells']>;

interface ResultInit {
  snapshot: Snapshot;
  seed: number;
  topic: string;
  topicLabel?: string;
  isDaily?: boolean;
  /** The daily twist this run was played under (-1/undefined = none) — minted into share links. */
  dailyMutatorId?: number;
  /** REAL threaded-eat count for this board (GameScene increments on gauntlet events). The old
   *  snapshot heuristic (armed-flag pairs at board end) massively undercounts because arming expires
   *  in 0.6s — it survives below only as a fallback for arg-less paths (review HIGH, 2026-07-02). */
  threadsThisBoard?: number;
  /** FACE & FURY S5: this board was a PERFECT GAUNTLET (won, zero decoys, ≥3 corrects, all threaded).
   *  Badges the share card + fronts the share prompt on the Result screen. */
  perfectGauntlet?: boolean;
  peakCombo?: number;
  ai: boolean;
  /** True for the menu-selected Rival warmup board; NEXT should start the actual race. */
  rivalWarmup?: boolean;
  boardIndex: number;
  boardSource?: BoardSource;
  noviceRun?: boolean;
  /** P1.2 challenge: the friend's score this run was launched to beat (0/undefined = not a challenge). */
  challengeTarget?: number;
  /** GHOST DUELS Slice C: the ladder challenger this daily run duelled (record + rung), or null. Drives the
   *  rung verdict banner + the persisted beaten-mark. Non-null only when the racer actually built. */
  ladderGhost?: LadderRace | null;
  /** GHOST DUELS Slice C: the human's MUNCH-SUM for this run (buildGhostTrack finalScore) — the rung verdict
   *  scores THIS against record.score (both munch-sums), never the full sim score. Set only on a ladder duel. */
  humanTrackScore?: number;
  /** P1.2c best-of-three series state, carried like challengeTarget (null = not a series). */
  series?: SeriesState | null;
}

export class ResultScene extends Phaser.Scene {
  args!: ResultInit;
  /** True when this Result is the decided-MATCH screen (NEW MATCH primary must fully reset the run). */
  matchComplete = false;
  recorded = false;
  reportPending = false;
  modalOpen = false;
  scoreTallyItems: Array<{ text: Phaser.GameObjects.Text; finalValue: number }> = [];
  scoreTallyTweens: Phaser.Tweens.Tween[] = [];
  scoreTallyComplete = true;
  idleAdvanceTimer?: Phaser.Time.TimerEvent;
  idleAdvanceText?: Phaser.GameObjects.Text;
  /** Explicit "tap to continue" affordance: TAP TO SKIP while the tally runs, the CTA destination once
   *  it lands. Paired with tap-anywhere (handleResultTap) so the result never lingers with no prompt. */
  tallyHint?: Phaser.GameObjects.Text;
  tallyLandedHint = 'TAP TO CONTINUE';
  /** Taps at/below this Y (the MENU/SHARE/RANKS trio band) are never treated as tap-anywhere. */
  tapGuardY = Number.POSITIVE_INFINITY;
  /** The primary CTA action, shared by the button, tap-anywhere, and ENTER/SPACE. */
  runPrimary?: () => void;
  /** STRETCH #16: the last-3s killcam model (null when the match is ineligible / the tail is too thin).
   *  Built once in create() from the final board's event-log tail; drives the auto-play, the REPLAY chip,
   *  and the share-sheet WebM export. */
  killcamModel?: KillcamModel | null;
  /** Spoiler-safe per-cell share route, enriched from the final event log so expired threaded decoys
   *  still appear on the in-app preview/export card. Fallbacks to final snapshot cells when unavailable. */
  shareRouteCells?: ShareRouteCells;
  /** S9 FIX-D: the primary CTA container + its idle pulse tween, kept so the killcam overlay can quiet
   *  them while it owns the screen (see setKillcamCaptionSuppressed). */
  primaryCta?: Phaser.GameObjects.Container;
  ctaPulse?: Phaser.Tweens.Tween;

  constructor() {
    super('Result');
  }

  init(data: ResultInit): void {
    this.args = data;
    this.recorded = false;
    this.reportPending = false;
    this.modalOpen = false;
    this.scoreTallyItems = [];
    this.scoreTallyTweens = [];
    this.scoreTallyComplete = true;
    this.idleAdvanceTimer = undefined;
    this.idleAdvanceText = undefined;
    this.tallyHint = undefined;
    this.tallyLandedHint = 'TAP TO CONTINUE';
    this.tapGuardY = Number.POSITIVE_INFINITY;
    this.runPrimary = undefined;
    this.killcamModel = undefined;
    this.shareRouteCells = undefined;
    this.primaryCta = undefined;
    this.ctaPulse = undefined;
  }

  create(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(PALETTE.bg);
    // VG-9: ease the entry so the gameplay->result hand-off isn't an abrupt hard cut (video gate nit).
    if (motionAllowed()) cam.fadeIn(240, 8, 10, 18);
    addBackdrop(this, 'result');
    const W = cam.width;
    const H = cam.height;
    const cx = W / 2;

    const snap = this.args.snapshot;
    this.shareRouteCells = this.buildShareRouteCells(snap);
    const human = snap.players.find((p) => p.id === 0);
    const ai = snap.players.find((p) => p.id === 1);
    const won = snap.status === 'won';
    const topicLabel = this.args.topicLabel || this.args.topic;
    const peakCombo = this.args.peakCombo ?? human?.combo ?? 0;
    const humanScore = human?.score ?? 0;
    const aiScore = ai?.score ?? 0;

    // COLOR CONTRACT (style-bible v2). The card speaks in a small fixed accent set so the eye knows
    // instantly what each thing IS: PALETTE.player = YOU, PALETTE.ai = the rival, PALETTE.correct =
    // the one CTA. PALETTE.gold = a WIN / rank-up (the trophy plate); PALETTE.hazard = loss;
    // PALETTE.armed = the daily-streak chip. Everything else is white/grey (text/textDim).

    // --- record stats + run ledger (exactly once) --------------------------
    const threadsThisBoard = this.args.threadsThisBoard ?? this.threadedFromSnapshot(snap);
    const board: RunBoard = {
      boardIndex: this.args.boardIndex,
      topicLabel,
      humanScore,
      aiScore: this.args.ai ? ai?.score ?? 0 : null,
      winner: this.args.ai ? snap.winner : won ? 0 : null,
      won,
      bestCombo: peakCombo,
      eatenCorrect: human?.eatenCorrect ?? 0,
      quota: snap.quota,
      threads: threadsThisBoard,
    };
    let rec: RecordResult | null = null;
    // GHOST DUELS Slice D: true only on the FIRST clear that opens today's ghost ladder — drives the
    // one-shot gatekeeper hand-off VO (barkLadderIntro) in the audio block below.
    let ladderJustUnlocked = false;
    // Captured BEFORE the recorded-guard flips it, so the BYT3 grudge match-write (which needs the
    // match-decided totals computed further down) can share the same once-per-result gate.
    const doRecord = !this.recorded;
    if (doRecord) {
      this.recorded = true;
      // P0.3: the novice/training run REPLACED the old stats-free tutorial overlay, so it must stay
      // stats-free. Recording it would pollute lifetime stats + per-topic bests ('training-*'),
      // inject training boards into the 5-board run ledger, and burn the one-time 'FIRST BOARD
      // CLEARED!' beat on a hand-holding board. Analytics (tutorial_step_completed) still fires below.
      if (!this.args.noviceRun) {
        rec = recordBoardOutcome({
          topicNormalized: this.args.topic,
          topicLabel,
          humanScore,
          bestCombo: peakCombo,
          won,
          isDaily: !!this.args.isDaily,
          gauntletsThreaded: threadsThisBoard,
        });
        appendRun(this, board);
        // P1.3 mastery: every correct answer the player CHOMPed feeds per-topic mastery + the
        // "facts learned" collection (dedup'd). Training boards are excluded (stats-free).
        this.recordMastery(snap);
        // GHOST DUELS Slice C: unlock the daily ghost ladder the moment today's daily is cleared WITH the
        // rival engaged. `snap.winner === 0` is the uniform "cleared the board (and out-scored BYT3 if it
        // was present)" signal in both modes; rival-engaged = the rival was on the board (args.ai) OR this
        // was the rival-mode warmup daily (BYT3 formally joins the next board). Idempotent + fail-closed
        // (a blocked store no-ops), and it never gates the streak credit above.
        if (this.args.isDaily && snap.winner === 0 && (this.args.ai || this.args.rivalWarmup)) {
          // Slice D: capture the false→true transition BEFORE the (idempotent) mark so the gatekeeper
          // hand-off line fires exactly once per day — a re-clear later today finds it already open.
          ladderJustUnlocked = !readLadder().gateBeaten;
          markGateBeaten();
        }
      }
    }
    const run = getRun(this);
    const totals = runTotals(run);
    // MATCH-LEVEL outcome (rival mode): on the final/clinching board it is the 5-board MATCH — not this
    // single board — that the player actually won or lost, so the match result drives the headline AND
    // the whole win/loss MOOD below. Without this, winning the match by losing the last board shows
    // loss-flavored confetti + mascot + headline (the #1 result-screen clarity bug). clinch = ⌊5/2⌋+1.
    const MATCH_BOARDS = 5;
    const matchClinch = Math.floor(MATCH_BOARDS / 2) + 1;
    const hasMatchProof =
      run.length >= MATCH_BOARDS ||
      totals.humanBoardWins >= matchClinch ||
      totals.aiBoardWins >= matchClinch;
    const matchDecided =
      this.args.ai &&
      !this.args.rivalWarmup &&
      hasMatchProof &&
      (this.args.boardIndex >= MATCH_BOARDS - 1 ||
        totals.humanBoardWins >= matchClinch ||
        totals.aiBoardWins >= matchClinch);
    const matchWon = totals.humanBoardWins > totals.aiBoardWins;
    const matchDrawn = totals.humanBoardWins === totals.aiBoardWins;
    const matchClinchedEarly = matchDecided && run.length < MATCH_BOARDS;
    // The celebratory mood to render: the MATCH result once it's decided, else this board's result.
    const triumph = matchDecided ? matchWon : won;
    // BYT3 GRUDGE LEDGER (V2-R1): at a real head-to-head match boundary, read the PRIOR grudge (for the
    // revenge/record framing), choose a relationship-aware caption for the match-end bark, then record
    // this match so the next opener/result reflects it. Renderer-only, once-per-result (shares doRecord),
    // never on warmup/novice/solo. VOICE only — never BYT3's Commands, never board contents.
    let grudgeMatchLine: string | undefined;
    if (doRecord && matchDecided && this.args.ai && !this.args.rivalWarmup && !this.args.noviceRun) {
      const matchThreads = run.reduce((n, b) => n + (b.threads ?? 0), 0);
      const outcome = { won: matchWon, drawn: matchDrawn, scoreMargin: totals.totalHumanScore - totals.totalAiScore, matchThreads };
      grudgeMatchLine = grudgeResultLine(readRivalMemory(), outcome) ?? undefined;
      recordRivalMatch(outcome);
    }
    if (!this.args.rivalWarmup && this.args.boardIndex >= 4) {
      // Log the MATCH result (triumph = match outcome in rival mode, board outcome solo) so completion
      // analytics match the headline the player sees instead of the last board's win/loss.
      track('run_completed', this.analyticsDims({ result: triumph ? 'won' : 'lost' }));
    }
    if (this.args.noviceRun) {
      track('tutorial_step_completed', {
        boardSource: 'seed',
        topicCategory: NOVICE_TOPIC_CATEGORY,
        difficulty: this.args.boardIndex + 1,
        boardIndex: this.args.boardIndex,
        result: won ? 'won' : 'lost',
      });
      if (isFinalNoviceBoard(this.args.boardIndex)) markOnboardingSeen();
    }
    const feelReviewDraft = buildFeelReviewDraft({
      registry: this.registry,
      seed: this.args.seed,
      topicLabel,
      run,
    });

    // --- audio + celebration ----------------------------------------------
    audio().attach(this);
    // Match-long music continuity: between boards the gameplay bed DIMS under the result stinger
    // instead of stopping — a stop restarted the track at 0:00 on every board, so fast players never
    // heard past the intro (B-section verification review, 2026-07-01). Full stop only once the
    // match is over (rival match decided, or the final board either mode).
    const matchOver = matchDecided || this.args.boardIndex >= MATCH_BOARDS - 1;
    if (matchOver) audio().stopMusic();
    else audio().setBedDim(true);
    // BYT3's result line (persona): board ends AND match ends fire HERE — GameScene's clock dies at
    // its 420ms Result handoff, so board-end barks scheduled there were dead code (review HIGH). The
    // delay lets the player's own fanfare own the moment first; resultSceneBark also captions the
    // line (accessibility contract: the text shows even under mute).
    if (this.args.ai && !this.args.rivalWarmup && !this.args.noviceRun) {
      // 1800, not 1600: the bark yields to any VO within 1600ms, and voYouWin stamps that clock at
      // create() — an exactly-equal constant would ride a one-frame margin (fix-verify note).
      if (ladderJustUnlocked) {
        // Slice D: beating BYT3 opens the ghost ladder — the gatekeeper hands you off to the day's
        // ghosts. This one-shot line OWNS the board-end voice this run (it replaces the generic bark).
        resultSceneBark(this, 'barkLadderIntro', 1900);
      } else if (!this.args.ladderGhost) {
        // On a ladder DUEL the gatekeeper's rung verdict (buildLadderVerdict) owns the voice instead,
        // so the generic board/match bark is suppressed here to avoid a double-bark on the same result.
        if (matchDecided) resultSceneBark(this, matchWon ? 'barkMatchLoss' : 'barkMatchWin', 1900, grudgeMatchLine);
        else resultSceneBark(this, won ? 'barkBoardLoss' : 'barkBoardWin', 1800);
      }
    }
    if (triumph) {
      audio().playSfx('boardClear', { volume: 0.9 });
      audio().playVo('voYouWin');
      // Restrained neon-palette confetti (fewer particles read cleaner / less "busy" than a dense
      // shower). Depth -5: ABOVE the backdrop (-9/-10) but BEHIND every result card/readout/link
      // (depth 0+), so the shower flutters in the background gaps and never buries the hero numbers.
      if (motionAllowed()) celebrate(this, [PALETTE.correct, PALETTE.player, PALETTE.ai, PALETTE.armed, PALETTE.gold], { count: 72, depth: -5 });
    } else {
      audio().playSfx('lifeLost', { volume: 0.7 });
    }

    // ── tactical OLED result surface (style-bible v2). Three variants share the same top→middle→dock
    // anchoring: hero pinned top, the summary flexes, the primary CTA + trio pinned to the thumb zone.
    const margin = aiScore - humanScore;
    const headline = this.args.rivalWarmup
      ? won
        ? 'WARMUP CLEAR'
        : 'WARMUP ENDED'
      : matchDecided
      ? matchWon
        ? matchClinchedEarly
          ? 'MATCH CLINCHED'
          : 'MATCH WON'
        : matchDrawn
        ? 'MATCH DRAWN'
        : 'MATCH LOST'
      : won
      ? 'BOARD CLEARED'
      : this.args.ai
      ? margin <= 150
        ? 'SO CLOSE!'
        : margin >= 600
        ? 'RIVAL RAN AWAY'
        : 'RIVAL TOOK IT'
      : 'TRY AGAIN';
    const screen: 'win' | 'loss' | 'match' = matchDecided ? 'match' : triumph ? 'win' : 'loss';
    // next() needs the match-complete fact after create() returns (NEW MATCH must start a FRESH match
    // at rung 0 with a clean run ledger — it used to re-enter at boardIndex 4 mid-run, verifier NIT).
    this.matchComplete = screen === 'match';
    this.playMetaSting(rec);

    // WIN vs LOSS CONTRAST (Gemini P0): a loss — board or match — is instantly SOMBER: a deep
    // desaturated red/gray wash over the backdrop (static; reduced-motion safe), muted panel
    // accents, a defeated mascot, quieter typography, and no confetti/gold anywhere. Color only
    // DECORATES the mood — headline copy, mascot pose, and the stat values still carry the outcome
    // for colorblind players. The win screen keeps its gold celebration untouched.
    const somber = !triumph;
    if (somber) {
      this.add.rectangle(cx, H / 2, W, H, 0x2a1219, 0.42).setDepth(-9); // desaturated maroon veil
      this.add.rectangle(cx, H / 2, W, H, 0x0a0c14, 0.38).setDepth(-9); // gray the field down
    }
    const panelStroke = somber ? PALETTE.line : PALETTE.lineStrong;
    const lossHeadline = 0xcf9aa3; // muted rose — far quieter than the win gold, still AA on the field

    // Accuracy / decoys / threaded — derived from the final snapshot (presentation only, no Core touch).
    let correctEaten = 0;
    let decoyEaten = 0;
    for (const c of snap.cells) {
      if (c.eatenBy === 0 && c.kind === 'correct') correctEaten += 1;
      else if (c.eatenBy === 0 && c.kind === 'decoy') decoyEaten += 1;
    }
    const quotaProgress = Math.max(correctEaten, human?.eatenCorrect ?? 0);
    const accDenom = correctEaten + decoyEaten;
    const accuracy = accDenom > 0 ? Math.round((correctEaten / accDenom) * 100) : triumph ? 100 : 0;
    const threaded = this.args.threadsThisBoard ?? this.threadedFromSnapshot(snap);
    const played = Math.min(run.length, MATCH_BOARDS);
    const fmtK = (n: number): string => (Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${Math.round(n)}`);
    const wideResult = W >= 1040 && W > H;
    const statW = wideResult ? Math.min(W * 0.44, 560) : Math.min(W * 0.92, 470);

    // Soft radial bloom behind the hero (gold on a win/match, faint red on a loss). Many low-alpha
    // ADDITIVE rings blend into a SMOOTH localized glow — never the discrete concentric "bull's-eye"
    // that 6 stacked discs produced. OLED-dark elsewhere (the backdrop is flat for result mode).
    const radial = (gy: number, color: number, peak: number): void => {
      const g = this.add.graphics().setDepth(0).setBlendMode(Phaser.BlendModes.ADD);
      const R = W * 0.46;
      const steps = 26;
      for (let i = steps; i >= 1; i--) {
        g.fillStyle(color, peak * 0.011);
        g.fillCircle(cx, gy, (R * i) / steps);
      }
    };
    // `quiet` = the loss treatment: no triumphant Back-ease pop — a slow fade-in instead (and the
    // caller passes a smaller size + dimmer glow), so defeat *types* softer than victory.
    const drawHeadline = (str: string, y: number, color: number, size: number, glow: number, blur: number, quiet = false): void => {
      const t = this.add
        .text(cx, y, str, { fontFamily: FONT.display, fontSize: `${scaledFont(size, 18)}px`, color: hex(color), fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(4)
        .setShadow(0, 0, hex(glow), blur, false, true);
      if (!motionAllowed()) return;
      if (quiet) {
        t.setAlpha(0);
        this.tweens.add({ targets: t, alpha: 1, duration: 560, ease: 'Sine.easeOut' });
      } else {
        t.setScale(0.6);
        this.tweens.add({ targets: t, scale: 1, duration: 440, ease: 'Back.easeOut' });
      }
    };
    // #4 — sub-headline bumped 13→15 (was crossed + shrunk by confetti on the PERFECT payoff).
    const drawSubline = (str: string, y: number, color: number = PALETTE.text): void => {
      this.add
        .text(cx, y, str, { fontFamily: FONT.family, fontSize: `${scaledFont(15, 12)}px`, color: hex(color), align: 'center', wordWrap: { width: W * 0.86 } })
        .setOrigin(0.5)
        .setDepth(4);
    };
    // FACE & FURY S5: a PERFECT GAUNTLET win (won, zero decoys, ≥3 corrects, all threaded) badges the
    // sub-headline gold + fronts the share prompt. perfectWin only fires on a triumphant screen (the
    // predicate already requires a board win), so it never lands on a loss/somber layout.
    const perfectWin = !!this.args.perfectGauntlet && triumph;
    // #30 propagate the in-board FLAWLESS accolade: a won board with zero decoys eaten AND ≥1 thread
    // mirrors eventFeedback's flawlessFeast — it earned the FLAWLESS hero stamp in-board, so the payoff
    // must not fall back to a generic BOARD CLEARED. PERFECT GAUNTLET is the strict superset and still
    // wins the headline; warmup/training keep their own framing.
    const flawlessWin =
      !perfectWin && won && !this.args.rivalWarmup && !this.args.noviceRun && decoyEaten === 0 && threaded >= 1;
    // #6 PHOTO FINISH persists to the payoff: a board decided within PHOTO_FINISH_MARGIN of the opponent
    // (the rival, or the ladder ghost in a duel — compared on the SAME munch-sum basis
    // GameScene.detectPhotoFinish uses) keeps its nail-biter identity here instead of reading as a
    // generic clear/loss. Never on solo/warmup/novice (no scored opponent stake).
    const photoOpp =
      this.args.ladderGhost != null
        ? Math.max(0, Math.floor(this.args.ladderGhost.record.score))
        : this.args.ai
          ? aiScore
          : null;
    const photoHuman = this.args.ladderGhost != null ? this.args.humanTrackScore ?? humanScore : humanScore;
    const photoMargin = photoOpp == null ? Number.POSITIVE_INFINITY : Math.abs(photoHuman - photoOpp);
    const photoFinish =
      !this.args.rivalWarmup && !this.args.noviceRun && photoOpp != null && photoMargin <= PHOTO_FINISH_MARGIN;
    // #38 graduation: the final mandatory training board completes the tutorial — celebrate it as a
    // distinct milestone (headline + UNLOCKED row) instead of another BOARD CLEARED. Training completes
    // whether that board is won or lost (markOnboardingSeen already fires unconditionally).
    const graduation = this.args.noviceRun && isFinalNoviceBoard(this.args.boardIndex);
    // #39 handshake: arm a one-shot "just graduated" flag the home hub reads on entry to run its
    // spotlight sweep over BUILD / DAILY / RIVAL. Renderer-only registry flag (MenuScene consumes +
    // clears it); set here because every exit from the training-final result lands on the home hub.
    if (graduation) this.registry.set('trivia.justGraduated.v1', true);
    const cardRect = (x: number, y: number, w: number, h: number, fill: number, stroke: number, sa: number, r = 14): void => {
      const g = this.add.graphics();
      g.fillStyle(fill, 1);
      g.fillRoundedRect(x - w / 2, y - h / 2, w, h, r);
      g.lineStyle(1.25, stroke, sa);
      g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, r);
    };
    const duelHalf = (x: number, y: number, h: number, label: string, labelColor: number, scoreColor: number, value: number | string, role: string): void => {
      labelText(this, x, y - h * 0.2, label, { color: labelColor, origin: 0.5, size: 11 });
      const t = this.add
        .text(x, y + h * 0.18, typeof value === 'number' ? '0' : value, { fontFamily: FONT.display, fontSize: `${typePx(this, 'display', { vw: 0.058, min: 22 })}px`, color: hex(scoreColor), fontStyle: 'bold' })
        .setOrigin(0.5);
      t.setData('resultScoreRole', role);
      if (typeof value === 'number') this.countUp(t, value);
    };

    // Bottom dock anchors (pinned to the thumb zone), keyed off scene height.
    // The secondary trio is 44px tall; position its center from a real bottom-edge margin instead of
    // `H - smallOffset`, so phones with gesture/home-indicator bands keep every button comfortably clear.
    const dockW = wideResult ? Math.min(W * 0.42, 520) : Math.min(W * 0.92, 470);
    const trioH = 44;
    const trioBottomMargin = Math.max(wideResult ? 52 : 56, H * (wideResult ? 0.055 : 0.062));
    const dockTrioY = H - trioBottomMargin - trioH / 2;
    const primaryY = dockTrioY - 58;
    // Protect the secondary trio (MENU / SHARE / RANKS, ~44px tall centred on dockTrioY) from a
    // tap-anywhere near-miss: taps at/below this line are left to the buttons themselves.
    this.tapGuardY = dockTrioY - 24;

    if (screen === 'match') {
      // S3b gap 4: match-complete rhythm spread DOWN to the win screen's cadence so the content fills
      // the column and the bottom dead band shrinks to win-parity (composition/spacing only).
      radial(H * 0.07, triumph ? PALETTE.gold : PALETTE.hazard, triumph ? 1 : 0.35);
      // #4 keep-out: only a triumphant match spawns confetti, so protect its headline+subline band.
      if (triumph) this.confettiKeepOut(cx, W, H * 0.15, H * 0.06);
      // Match WON keeps the gold trophy moment; match LOST swaps it for the defeated mascot + a
      // quieter, fade-in headline (the P0 win/loss contrast, at the match level too).
      if (triumph) {
        icon(this, 'trophy', cx, H * 0.06, 38, PALETTE.gold).setDepth(4);
        drawHeadline(headline, H * 0.13, PALETTE.gold, 30, PALETTE.gold, 18);
      } else {
        this.defeatedMascot(cx, H * 0.062, 54);
        drawHeadline(headline, H * 0.13, lossHeadline, 24, PALETTE.hazard, 4, true);
      }
      if (perfectWin) drawSubline('PERFECT GAUNTLET — SHARE YOUR FLAWLESS RUN', H * 0.175, PALETTE.gold);
      else if (flawlessWin) drawSubline('FLAWLESS FINAL BOARD — SHARE YOUR CLEAN RUN', H * 0.175, PALETTE.gold);
      else if (photoFinish) drawSubline(`PHOTO FINISH — decided by ${photoMargin} · Best of 5`, H * 0.175, PALETTE.gold);
      // #52 residual (S9 FIX-D): the match-end header was the LAST surface still routing through the
      // mid-word ellipsis truncate ("2026 Tournament Host Citi…" read as "Citi"). Word-boundary shorten
      // with NO ellipsis glyph — the S9 discipline every other topic line already uses — with the cap
      // keyed on the LOGICAL stage width (phones render at 2× logical, so W≥700 covers desktop AND
      // modern portrait — verified the 27-char topic fits one line on 412×915; only genuinely narrow
      // logical stages keep the tight cap). drawSubline's wordWrap owns any true overflow, ellipsis-free.
      else if (matchClinchedEarly) drawSubline(`Clinched ${totals.humanBoardWins}-${totals.aiBoardWins} · ${shortenAtWord(topicLabel, W >= 700 ? 44 : 22)}`, H * 0.175);
      else drawSubline(`Best of 5 · ${shortenAtWord(topicLabel, W >= 700 ? 48 : 26)}`, H * 0.175);

      const tH = Math.max(90, H * 0.135);
      const tY = H * 0.285;
      cardRect(cx, tY, statW, tH, PALETTE.surface, panelStroke, 1, 16);
      const youX = cx - statW * 0.25;
      const rivalX = cx + statW * 0.25;
      icon(this, 'crown', youX, tY - tH * 0.3, 15, somber ? PALETTE.dim : PALETTE.gold);
      duelHalf(youX, tY + tH * 0.02, tH, 'YOU', PALETTE.player, PALETTE.text, humanScore, 'you');
      this.add.rectangle(cx, tY, 1.5, tH - 22, PALETTE.lineStrong, 0.7);
      duelHalf(rivalX, tY + tH * 0.02, tH, 'RIVAL', PALETTE.rival, PALETTE.dim, this.args.ai ? aiScore : 0, 'rival');
      // One win-indicator treatment shared with board-win (S3 item 22): the same centered pill between
      // the two scores. Gold stays reserved for a WIN; a draw/loss uses armed/hazard, ink label.
      chip(this, cx, tY, matchWon ? 'WON' : matchDrawn ? 'DRAW' : 'LOST', {
        textColor: PALETTE.ink,
        fill: matchWon ? PALETTE.gold : matchDrawn ? PALETTE.armed : PALETTE.hazard,
        borderColor: matchWon ? PALETTE.gold : matchDrawn ? PALETTE.armed : PALETTE.hazard,
        originX: 0.5,
        fontPx: 12,
        height: 22,
      });

      // Show BOTH board-win counts so the strip can never contradict the MATCH WON/LOST headline
      // (S3 item 23): "YOU 3 · RIVAL 2" reads unambiguously with any headline.
      labelText(this, cx - statW / 2, H * 0.39, `BOARD BY BOARD · YOU ${totals.humanBoardWins} · RIVAL ${totals.aiBoardWins}`, { size: 11, color: PALETTE.dim });
      const mcY = H * 0.465;
      const mcGap = 8;
      const mcW = (statW - mcGap * (MATCH_BOARDS - 1)) / MATCH_BOARDS;
      const mcH = Math.max(58, H * 0.085);
      let mcx = cx - statW / 2 + mcW / 2;
      for (let i = 0; i < MATCH_BOARDS; i++) {
        const b = i < played ? run[i] : undefined;
        const youTook = b ? (this.args.ai ? b.winner === 0 : b.won) : false;
        const g = this.add.graphics();
        g.fillStyle(PALETTE.surface2, b ? 1 : 0.5);
        g.fillRoundedRect(mcx - mcW / 2, mcY - mcH / 2, mcW, mcH, 10);
        g.lineStyle(1.25, b ? (youTook ? PALETTE.correct : PALETTE.hazard) : PALETTE.line, b ? (youTook ? 0.4 : 0.35) : 1);
        g.strokeRoundedRect(mcx - mcW / 2, mcY - mcH / 2, mcW, mcH, 10);
        labelText(this, mcx, mcY - mcH * 0.3, `B${i + 1}`, { size: 9, color: PALETTE.dim, origin: 0.5 });
        if (b) {
          icon(this, youTook ? 'checkCircle' : 'xCircle', mcx, mcY, 15, youTook ? PALETTE.correct : PALETTE.hazard);
          this.add
            .text(mcx, mcY + mcH * 0.32, fmtK(b.humanScore), { fontFamily: FONT.display, fontSize: `${scaledFont(11, 10)}px`, color: hex(PALETTE.text), fontStyle: 'bold' })
            .setOrigin(0.5);
        } else {
          // Explicit "not played" dash so an empty rung reads as skipped, not as a lost board.
          this.add
            .text(mcx, mcY + mcH * 0.02, '—', { fontFamily: FONT.display, fontSize: `${scaledFont(16, 13)}px`, color: hex(PALETTE.mute), fontStyle: 'bold' })
            .setOrigin(0.5);
        }
        mcx += mcW + mcGap;
      }

      // Somber (match lost/drawn): value accents mute to grey — no gold, no celebratory hues.
      statTileRow(this, cx, H * 0.6, statW, [
        { value: `${accuracy}%`, label: 'ACCURACY', color: somber ? PALETTE.textDim : PALETTE.correct },
        { value: `×${peakCombo}`, label: 'BEST COMBO', color: somber ? PALETTE.textDim : PALETTE.armed },
        { value: `+${fmtK(rec?.xpGained ?? 0)}`, label: 'XP EARNED', color: somber ? PALETTE.textDim : PALETTE.gold },
      ], { height: 58 });
    } else if (screen === 'win') {
      radial(H * 0.1, PALETTE.gold, 1);
      // #4 keep-out: the win screen always celebrates (confetti) — protect the headline+subline band.
      this.confettiKeepOut(cx, W, H * 0.205, H * 0.065);
      this.victoryMascot(cx, H * 0.1, 76);
      // Item 7 — a PERFECT GAUNTLET is the best outcome in the game, so it OWNS the hero headline (gold)
      // and 'BOARD CLEARED' drops to the sub-line. It never lands on a loss (perfectWin already requires
      // the board win); a MATCH-decided screen keeps 'MATCH WON' as the bigger stake (handled above).
      // #38/#30/#6: the payoff headline escalates past the generic BOARD CLEARED — graduation ▸ perfect
      // ▸ flawless ▸ photo-finish ▸ default — so a signature clear never reads as any other clear.
      const winHeadline = graduation
        ? 'TRAINING COMPLETE'
        : perfectWin
          ? 'PERFECT GAUNTLET'
          : flawlessWin
            ? 'FLAWLESS'
            : photoFinish
              ? 'PHOTO FINISH'
              : headline;
      const winAccolade = graduation || perfectWin || flawlessWin || photoFinish;
      drawHeadline(winHeadline, H * 0.185, PALETTE.gold, winAccolade ? 26 : 30, PALETTE.gold, 18);
      if (graduation) drawSubline('Every verb learned — the full game is now open', H * 0.232, PALETTE.gold);
      else if (perfectWin) drawSubline('BOARD CLEARED · every answer threaded — SHARE YOUR FLAWLESS RUN', H * 0.232, PALETTE.gold);
      else if (flawlessWin) drawSubline('Zero decoys eaten — a clean route. SHARE THE FLAWLESS BOARD', H * 0.232, PALETTE.gold);
      else if (photoFinish) drawSubline(`Edged the rival by ${photoMargin} — SHARE THE PHOTO FINISH`, H * 0.232, PALETTE.gold);
      else drawSubline(this.args.rivalWarmup ? 'Rival joins next board' : this.args.ai ? 'You out-munched the rival' : 'Quota met!', H * 0.232);

      const duelH = Math.max(74, H * 0.108);
      const duelY = H * 0.335;
      cardRect(cx, duelY, statW, duelH, PALETTE.surface, PALETTE.lineStrong, 1, 14);
      this.add.rectangle(cx, duelY, 1.5, duelH - 16, PALETTE.lineStrong, 0.7);
      duelHalf(cx - statW * 0.26, duelY, duelH, 'YOU', PALETTE.player, PALETTE.text, humanScore, 'you');
      if (this.args.ai) {
        duelHalf(cx + statW * 0.26, duelY, duelH, 'RIVAL', PALETTE.rival, PALETTE.dim, aiScore, 'rival');
        chip(this, cx, duelY, won ? 'WIN' : 'LOSS', { textColor: PALETTE.ink, fill: PALETTE.gold, borderColor: PALETTE.gold, originX: 0.5, fontPx: 12, height: 22 });
      } else {
        duelHalf(cx + statW * 0.26, duelY, duelH, 'GOAL', PALETTE.correct, PALETTE.text, `${human?.eatenCorrect ?? 0}/${snap.quota}`, 'goal');
      }

      statTileRow(this, cx, H * 0.45, statW, [
        { value: `${accuracy}%`, label: 'ACCURACY', color: PALETTE.correct },
        { value: `×${peakCombo}`, label: 'BEST COMBO', color: PALETTE.armed },
        { value: `${threaded}`, label: 'THREADED', color: PALETTE.armed },
      ], { height: 58 });

      // #38: the 5-board RUN strip is meaningless on a training run (novice boards are never appended to
      // the run ledger, so it read an empty "RUN 0/5"). Suppress it entirely on novice boards.
      if (!this.args.noviceRun) {
        const runY = H * 0.53;
        const pillW = Math.min(30, statW * 0.06);
        const pillH = 12;
        const pillGap = 7;
        const groupW = MATCH_BOARDS * pillW + (MATCH_BOARDS - 1) * pillGap;
        let px = cx - groupW / 2;
        for (let i = 0; i < MATCH_BOARDS; i++) {
          const b = i < played ? run[i] : undefined;
          const youTook = b ? (this.args.ai ? b.winner === 0 : b.won) : false;
          const g = this.add.graphics();
          g.fillStyle(b ? (youTook ? PALETTE.correct : PALETTE.hazard) : PALETTE.tile, b ? 0.9 : 0.4);
          g.fillRoundedRect(px, runY - pillH / 2, pillW, pillH, 3);
          if (!b) {
            g.lineStyle(1, PALETTE.line, 1);
            g.strokeRoundedRect(px, runY - pillH / 2, pillW, pillH, 3);
          }
          px += pillW + pillGap;
        }
        labelText(this, cx - groupW / 2 - 12, runY, 'RUN', { size: 11, color: PALETTE.dim, origin: 1 });
        this.add
          .text(cx + groupW / 2 + 12, runY, `${played}/${MATCH_BOARDS}`, { fontFamily: FONT.display, fontSize: `${scaledFont(13, 11)}px`, color: hex(PALETTE.dim), fontStyle: 'bold' })
          .setOrigin(0, 0.5);
      }

      const ribbonY = H * 0.61;
      if (graduation) {
        // #38: light the three affordances the tutorial promised — the graduation payoff owns this band.
        this.buildGraduationUnlocks(cx, W, ribbonY);
      } else if (rec?.rankedUp) {
        rankRibbon(this, cx, ribbonY, statW, {
          iconName: 'crown',
          title: `RANK UP — ${rec.rankName}`,
          sub: 'New rank unlocked',
          xp: rec.xpGained > 0 ? `+${rec.xpGained} XP` : undefined,
          height: 58,
        });
      } else {
        const ach = this.achievementText(rec);
        if (ach) chip(this, cx, ribbonY, ach, { icon: 'star', textColor: PALETTE.gold, borderColor: PALETTE.gold, fill: PALETTE.surface, originX: 0.5, fontPx: 12 });
        else if (rec && rec.xpGained > 0) chip(this, cx, ribbonY, `+${rec.xpGained} XP`, { icon: 'bolt', textColor: PALETTE.armed, borderColor: PALETTE.armed, fill: PALETTE.surface, originX: 0.5, fontPx: 12 });
      }
    } else {
      // S3b gap 4: board-loss rhythm spread DOWN to the win screen's cadence so the content fills the
      // column and the bottom dead band shrinks to win-parity (composition/spacing only).
      radial(H * 0.1, PALETTE.hazard, 0.35);
      this.defeatedMascot(cx, H * 0.1, 62);
      // #6: a photo-finish LOSS keeps its nail-biter identity ("PHOTO FINISH") rather than the flatter
      // SO CLOSE / RIVAL TOOK IT, and fronts a share prompt so the near-miss is still clip-worthy.
      const lossHead = photoFinish ? 'PHOTO FINISH' : headline;
      drawHeadline(lossHead, H * 0.185, lossHeadline, photoFinish ? 26 : 24, PALETTE.hazard, 4, true);
      drawSubline(
        photoFinish
          ? `The rival edged it by ${photoMargin} — so close. Share the photo finish?`
          : this.args.ai && margin > 0
            ? `The rival edged this board by ${margin}`
            : this.args.ai
              ? 'The rival took this board'
              : 'Time / lives ran out',
        H * 0.232,
      );

      const duelH = Math.max(74, H * 0.108);
      const duelY = H * 0.335;
      cardRect(cx, duelY, statW, duelH, PALETTE.surface, panelStroke, 1, 14);
      duelHalf(cx - statW * 0.26, duelY, duelH, 'YOU', PALETTE.player, PALETTE.text, humanScore, 'you');
      duelHalf(
        cx + statW * 0.26,
        duelY,
        duelH,
        this.args.ai ? 'RIVAL' : 'GOAL',
        this.args.ai ? PALETTE.rival : PALETTE.correct,
        this.args.ai ? PALETTE.rival : PALETTE.text,
        this.args.ai ? aiScore : `${human?.eatenCorrect ?? 0}/${snap.quota}`,
        this.args.ai ? 'rival' : 'goal',
      );
      if (this.args.ai && margin > 0) {
        // Margin delta ATTACHED to the rival side (labeled), never floating between the two numbers
        // (S3 item 24): a small hazard chip under the RIVAL column reads "RIVAL +190" as the lead.
        // #48 — anchored to the card BOTTOM EDGE (straddling it) instead of duelY+0.42H: on the short
        // landscape card (duelH floors at 74) the 0.42H slot sat ON the RIVAL score. The bottom-edge
        // seat clears the score in every aspect while keeping the under-RIVAL attribution.
        chip(this, cx + statW * 0.26, duelY + duelH / 2, `+${margin}`, {
          icon: 'arrowUp',
          textColor: PALETTE.hazard,
          borderColor: PALETTE.hazard,
          fill: PALETTE.surface2,
          originX: 0.5,
          fontPx: 11,
          height: 20,
        });
      }

      // Muted accents on the loss card: grey values — only DECOYS EATEN keeps the danger hue (it is
      // the actionable "what went wrong" signal the coach tip below picks up).
      statTileRow(this, cx, H * 0.45, statW, [
        { value: `${accuracy}%`, label: 'ACCURACY', color: PALETTE.textDim },
        { value: `×${peakCombo}`, label: 'BEST COMBO', color: PALETTE.textDim },
        { value: `${decoyEaten}`, label: 'DECOYS EATEN', color: PALETTE.hazard },
      ], { height: 58 });

      const coach = this.resultCoachLines({
        won,
        eatenCorrect: human?.eatenCorrect ?? 0,
        quota: snap.quota,
        lives: human?.lives ?? 0,
        timeLeftMs: snap.timeLeftMs,
        humanScore,
        aiScore,
        peakCombo,
      });
      const noteW = statW;
      const noteH = Math.max(74, H * 0.11);
      const noteY = H * 0.565;
      // Card token (S3 item 3): the tip card shares the one cardPanel treatment (border weight/radius
      // + left accent-rail) with the NEXT UNLOCK row and the rank ribbon.
      cardPanel(this, cx, noteY, noteW, noteH, { fill: PALETTE.surface, border: PALETTE.line, accent: PALETTE.scan });
      icon(this, 'lightbulb', cx - noteW / 2 + 24, noteY - noteH / 2 + 22, 16, PALETTE.scan);
      this.add
        .text(cx - noteW / 2 + 40, noteY - noteH / 2 + 22, 'Tip — thread, don’t rush', { fontFamily: FONT.display, fontSize: `${scaledFont(13, 12)}px`, color: hex(PALETTE.text), fontStyle: 'bold' })
        .setOrigin(0, 0.5);
      const body = decoyEaten > 0 ? `You ate ${decoyEaten} decoy${decoyEaten === 1 ? '' : 's'} under pressure. Route through them for the ×1.5 instead.` : coach.next;
      this.add
        .text(cx - noteW / 2 + 24, noteY - noteH / 2 + 44, body, { fontFamily: FONT.family, fontSize: `${scaledFont(12, 11)}px`, color: hex(PALETTE.dim), align: 'left', wordWrap: { width: noteW - 44 } })
        .setOrigin(0, 0);
    }

    const supportBandY = wideResult ? H * 0.71 : Math.min(primaryY - 104, H * 0.75);

    // GHOST DUELS Slice C: a ladder-duel verdict (out-ran the challenger's ghost, or fell short) owns the
    // NEXT-UNLOCK band on a ladder run and persists the rung's beaten-mark for the panel's check state.
    const ladderShown = this.buildLadderVerdict(cx, W, supportBandY, humanScore);

    // NEXT-GOAL PULL (retention): one compact "NEXT UNLOCK" row under the XP/summary block — the
    // nearest concrete goal (topic mastery tier → cosmetic skin → daily streak day) with a live
    // progress bar, filling the dead bottom band at phone aspect with a reason to play again.
    // Skipped on training boards (stats-free) and when a challenge / ladder verdict owns the same band.
    if (!this.args.noviceRun && this.challengeVerdictHeight() === 0 && !ladderShown) {
      this.buildNextUnlock(cx, W, supportBandY, somber);
    }

    if (!wideResult && screen === 'win' && !this.args.noviceRun && !ladderShown && this.challengeVerdictHeight() === 0) {
      const nextUnlockH = W < H && H >= 1400 ? 96 : 46;
      const primaryTopY = primaryY - 32;
      const gapTop = supportBandY + nextUnlockH / 2 + 14;
      const gapBottom = primaryTopY - 16;
      if (gapBottom - gapTop >= 48) {
        this.buildResultRunRecap(cx, W, (gapTop + gapBottom) / 2, Math.min(58, gapBottom - gapTop), {
          played,
          runTotal: MATCH_BOARDS,
          peakCombo,
          threaded,
          accuracy,
        });
      }
    }

    // #38: on the rarer training-final LOSS the ribbon band is unused — surface the same UNLOCKED payoff
    // in the free next-unlock band so finishing training always acknowledges the game opening up.
    if (graduation && screen === 'loss') this.buildGraduationUnlocks(cx, W, supportBandY);

    // Challenge / best-of-three verdict (only when this board was launched from a friend's link).
    // #31: when the WIN screen already fills the ribbon band (H*0.61) with a RANK UP ribbon or an
    // achievement/XP chip, drop the verdict to the otherwise-free NEXT-UNLOCK band (H*0.71) so the two
    // never overlap. Otherwise keep it snug under the summary at H*0.64.
    if (this.challengeVerdictHeight() > 0) {
      const winRibbonBandUsed =
        screen === 'win' &&
        !graduation &&
        !!rec &&
        (rec.rankedUp || this.achievementText(rec) !== '' || rec.xpGained > 0);
      this.buildChallengeVerdict(cx, W, winRibbonBandUsed ? supportBandY : H * 0.64, humanScore);
    }

    if (wideResult) {
      this.buildDesktopResultRails({
        cx,
        W,
        H,
        statW,
        screen,
        won,
        topicLabel,
        rule: snap.rule,
        boardIndex: this.args.boardIndex,
        quota: snap.quota,
        eatenCorrect: quotaProgress,
        accuracy,
        peakCombo,
        threaded,
        humanScore,
        aiScore,
        decoyEaten,
        played,
        runTotal: MATCH_BOARDS,
        somber,
      });
    }

    // ── bottom dock: one hero CTA + a secondary trio, pinned to the thumb zone ──
    const goMenu = (): void => {
      this.cancelIdleAdvance();
      resetFeelReviewSession(this.registry);
      resetRun(this);
      this.scene.start('Menu');
      this.scene.stop('Result');
    };
    const goLeaderboard = (): void => {
      this.cancelIdleAdvance();
      this.modalOpen = true;
      openLeaderboard(this, {
        submit: { name: '', score: humanScore, topicLabel, topicNormalized: this.args.topic, isDaily: !!this.args.isDaily, seed: this.args.seed },
        topicNormalized: this.args.topic,
        initialScope: this.args.isDaily ? 'daily' : 'topic',
        onClose: () => {
          this.modalOpen = false;
        },
      });
    };
    const goShare = (): void => {
      this.cancelIdleAdvance();
      this.modalOpen = true;
      const ref: { sm?: ReturnType<typeof openShareSheet> } = {};
      const fire = (): void => {
        ref.sm?.close();
        this.onShare(board, peakCombo, won);
      };
      // #26: hand the sheet the REAL share-card data so its preview renders the shipped 9:16 portrait
      // route card (share.ts) rather than the cyan/no-grid mock. Mirrors the visual-relevant fields
      // resultReport.onShare feeds shareResult(), so the preview matches the export byte-for-byte.
      const previewCard: ShareCardData = {
        topicLabel,
        rule: snap.rule,
        score: humanScore,
        bestCombo: peakCombo,
        eatenCorrect: board.eatenCorrect,
        quota: snap.quota,
        won,
        seed: this.args.seed,
        topicNormalized: this.args.topic,
        isDaily: !!this.args.isDaily,
        dailyMutatorId: this.args.dailyMutatorId,
        boardIndex: this.args.boardIndex,
        cells: this.shareRouteCells ?? snap.cells.map((c) => ({ kind: c.kind, eaten: c.eaten, eatenBy: c.eatenBy, armed: c.armed })),
        rows: snap.rows,
        cols: snap.cols,
        hasRival: this.args.ai,
        beatRival: this.args.ai ? snap.winner === 0 : false,
        perfectGauntlet: this.args.perfectGauntlet,
        // S9 FIX-D: the REAL gauntlet-event thread tally — keeps the card legend honest against the
        // Result's THREADED stat (the snapshot's armed cells expire and under-report).
        threads: threadsThisBoard,
      };
      // STRETCH #16 B2: offer the killcam WebM ALONGSIDE the still card — only when a killcam model
      // exists AND the platform can capture a canvas to webm. Absent support → the button never appears
      // (no broken affordance); the still share path is unchanged. shareKillcamVideo falls back to the
      // still (shareResult) on any record/share failure.
      const kc = this.killcamModel;
      const videoReady = !!kc && killcamWebMSupported();
      const shareVideo = videoReady
        ? (): void => {
            ref.sm?.close();
            this.toast('Rendering clip…');
            void shareKillcamVideo(kc as KillcamModel, previewCard, shareResult);
          }
        : undefined;
      ref.sm = openShareSheet(this, {
        topic: topicLabel,
        score: humanScore,
        onShare: fire,
        onCopy: fire,
        onSave: fire,
        onShareVideo: shareVideo,
        canShareVideo: videoReady,
        perfectGauntlet: this.args.perfectGauntlet,
        card: previewCard,
      });
      ref.sm.layer.once(Phaser.GameObjects.Events.DESTROY, () => {
        this.modalOpen = false;
      });
    };

    const primaryLabel =
      this.seriesPrimaryLabel() ??
      (this.args.rivalWarmup
        ? 'START RACE'
        : this.args.noviceRun && isFinalNoviceBoard(this.args.boardIndex)
          ? 'OPEN HOME'
          : screen === 'match'
            ? 'NEW MATCH'
            : won
              ? 'NEXT BOARD'
              : 'TRY AGAIN');
    const primaryIcon = screen === 'win' ? 'caretRight' : 'again';
    // The one primary action, shared by the CTA button, tap-anywhere, and ENTER/SPACE so all three do
    // EXACTLY the same thing (next board / new match / series share).
    const activatePrimary = (): void => {
      this.cancelIdleAdvance();
      this.skipScoreTally();
      track('next_board_tapped', this.analyticsDims({ result: this.args.rivalWarmup ? 'start_race' : this.args.noviceRun ? 'training_continue' : 'continue' }));
      // P1.2c: in a series SET phase (SEND NEXT) the primary CTA SHARES the board just played as the
      // friend's next game — NOT a silent return to menu that would drop the turn (#6 fix).
      if (this.args.series && Math.max(0, Math.floor(this.args.challengeTarget ?? 0)) <= 0) {
        this.onShare(board, peakCombo, won);
        return;
      }
      this.next();
    };
    this.runPrimary = activatePrimary;
    // Landed-tally hint copy mirrors the CTA destination: noun labels (NEXT BOARD / NEW MATCH / NEXT
    // GAME) read "TAP FOR …"; verb-led labels (TRY AGAIN / OPEN HOME / SEND NEXT / FINISH) read "TAP TO …".
    this.tallyLandedHint = `${/^(NEXT|NEW)\b/.test(primaryLabel) ? 'TAP FOR' : 'TAP TO'} ${primaryLabel}`;
    const primaryCta = ctaButton(
      this,
      cx,
      primaryY,
      primaryLabel,
      activatePrimary,
      { width: dockW, height: 60, icon: primaryIcon, fill: PALETTE.correct },
    );
    this.pulsePrimaryCta(primaryCta);
    // While the tally is still counting, keep an explicit skip hint. Once landed, the large CTA is enough;
    // a second tiny "TAP FOR NEXT BOARD" caption created the exact low-contrast dead gap the still gate
    // flagged on tall phones.
    if (!this.scoreTallyComplete) {
      const hintY = primaryY - (feelReviewDraft ? 74 : 42);
      this.tallyHint = this.add
        .text(cx, hintY, 'TAP TO SKIP', {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(11, 10)}px`,
          color: hex(PALETTE.text),
          fontStyle: '600',
        })
        .setOrigin(0.5)
        .setStroke('#050812', 3)
        .setShadow(0, 1, '#050812', 2, false, true)
        .setDepth(6);
      if (motionAllowed()) {
        this.tweens.add({ targets: this.tallyHint, alpha: 0.5, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      }
    }

    const trioGap = 10;
    const trioW = (dockW - trioGap * 2) / 3;
    const firstLabelStr = screen === 'win' ? 'AGAIN' : screen === 'loss' ? 'MENU' : 'HOME';
    const firstIcon = screen === 'win' ? 'again' : 'house';
    secondaryButton(this, cx - trioW - trioGap, dockTrioY, firstLabelStr, goMenu, { width: trioW, height: trioH, icon: firstIcon, fontPx: 14 });
    // #6/#30: a signature clear (perfect / flawless / photo-finish) fronts the SHARE control in gold so
    // the eye is pulled to the brag; a routine result keeps the calm scan tint.
    const shareHot = perfectWin || flawlessWin || photoFinish;
    const shareBtn = secondaryButton(this, cx, dockTrioY, 'SHARE', goShare, { width: trioW, height: trioH, icon: 'share', textColor: shareHot ? PALETTE.gold : PALETTE.scan, fontPx: 14 });
    secondaryButton(this, cx + trioW + trioGap, dockTrioY, 'RANKS', goLeaderboard, { width: trioW, height: trioH, icon: 'ranking', textColor: PALETTE.armed, fontPx: 14 });
    // FACE & FURY S5: front the share prompt on a PERFECT GAUNTLET — a gold, softly-pulsing halo around
    // the SHARE control so the eye is pulled to it (paired with the gold sub-headline prompt above). A
    // sibling graphic (never a child of the button) so it can't shift the trio layout; motion-gated.
    if (perfectWin && shareBtn) {
      const halo = this.add.graphics().setDepth(shareBtn.depth - 1);
      halo.lineStyle(2, PALETTE.gold, 0.9);
      halo.strokeRoundedRect(cx - trioW / 2 - 3, dockTrioY - 25, trioW + 6, 50, 12);
      if (motionAllowed()) {
        this.tweens.add({ targets: halo, alpha: 0.25, duration: 720, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      }
    }

    // Low-prominence REPORT affordance — reachable without competing, but legible (S3 item 20): a flag
    // glyph + a dim (not mute) label at ≥4.5:1, with a full ≥44px tap target. Kept top-right (the
    // conventional report corner) since the bottom dock is fully occupied by the CTA + trio.
    const reportY = H * 0.05;
    const reportTxt = this.add
      .text(W - 16, reportY, 'REPORT', { fontFamily: FONT.display, fontSize: `${scaledFont(12, 11)}px`, color: hex(PALETTE.dim), fontStyle: '600' })
      .setOrigin(1, 0.5)
      .setDepth(6);
    const reportGlyph = icon(this, 'info', W - 16 - reportTxt.width - 10, reportY, 14, PALETTE.dim).setDepth(6);
    const reportHit = this.add
      .rectangle(W - 16 - (reportTxt.width + 24) / 2, reportY, reportTxt.width + 48, 44, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true })
      .setDepth(6);
    const paintReport = (c: number): void => {
      reportTxt.setColor(hex(c));
      reportGlyph.clear();
      drawIcon(reportGlyph, 'info', 14, c);
    };
    reportHit.on('pointerover', () => paintReport(PALETTE.text));
    reportHit.on('pointerout', () => paintReport(PALETTE.dim));
    reportHit.on('pointerup', () => {
      this.cancelIdleAdvance();
      this.openReportModal(topicLabel);
    });

    // Playtest CSV draft (observer tool) — only present in a feel-review session; just above the CTA.
    if (feelReviewDraft) {
      secondaryButton(this, cx, primaryY - 44, 'COPY TEST ROW', () => {
        this.cancelIdleAdvance();
        this.openFeelReviewDraftModal(feelReviewDraft);
      }, { width: dockW, height: 30, icon: 'copy', textColor: PALETTE.scan, fontPx: 12 });
    }

    this.input.on('pointerdown', this.handleResultTap, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cancelIdleAdvance();
      this.input.off('pointerdown', this.handleResultTap, this);
    });

    // Keyboard mirrors tap-anywhere: first press lands the tally, the next runs the primary action.
    const advanceOrSkip = (): void => {
      if (this.modalOpen) return;
      this.cancelIdleAdvance();
      if (this.skipScoreTally()) return;
      activatePrimary();
    };
    this.input.keyboard?.on('keydown-ENTER', advanceOrSkip);
    this.input.keyboard?.on('keydown-SPACE', advanceOrSkip);

    // STRETCH #16 KILLCAM — the last-3s route replay. Built from the final board's event-log tail, it
    // plays once on entry for rival / photo-finish / perfect results (never warmup/novice) and stays
    // re-runnable via the REPLAY chip. Deferred so the entry fade + headline land first.
    this.setupKillcam(snap, { won, perfectWin, photoFinish, photoMargin, topicLabel });
  }

  /**
   * #4 — a soft dark keep-out band behind a triumphant hero headline+subline so the background confetti
   * shower (depth -5) never crosses the text and dilutes its contrast. Sits at depth 3 (above the
   * confetti, below the depth-4 headline/subline). Feathered via three stacked low-alpha rounded rects;
   * fully static, so it is reduceMotion- and colorblind-safe (it only darkens, never signals).
   */
  confettiKeepOut(cx: number, W: number, centerY: number, halfH: number): void {
    const bandW = Math.min(W * 0.92, 520);
    const layers = [
      { padX: 40, padY: 26, a: 0.16 },
      { padX: 20, padY: 12, a: 0.22 },
      { padX: 0, padY: 0, a: 0.32 },
    ];
    for (const l of layers) {
      this.add
        .graphics()
        .setDepth(3)
        .fillStyle(PALETTE.void, l.a)
        .fillRoundedRect(cx - bandW / 2 - l.padX, centerY - halfH - l.padY, bandW + l.padX * 2, halfH * 2 + l.padY * 2, 22);
    }
  }

  pulsePrimaryCta(cta: Phaser.GameObjects.Container): void {
    // Kept (even under reduced motion) so the killcam overlay can quiet the CTA band (S9 FIX-D).
    this.primaryCta = cta;
    if (!motionAllowed()) return;
    this.ctaPulse = this.tweens.add({
      targets: cta,
      scaleX: 1.025,
      scaleY: 1.06,
      duration: 740,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      delay: 700,
    });
  }

  /**
   * S9 FIX-D (G2 AMATEUR 1): while the killcam overlay is up, its "TAP TO SKIP" hint lands at the same
   * y as the Result's own "TAP FOR NEXT BOARD" caption on the landscape stage — the two read as smeared
   * double text through the scrim, exactly where the eye is sent for the interaction hint. Hide the
   * caption (and freeze the pulsing NEXT-BOARD CTA glow at rest) for the overlay's life; restore on
   * close. Result-side implementation of the established suppress pattern (hudController's stamp-yield
   * discipline) — the overlay module itself stays untouched.
   */
  setKillcamCaptionSuppressed(on: boolean): void {
    if (this.tallyHint?.active) this.tallyHint.setVisible(!on);
    if (this.ctaPulse) {
      if (on) this.ctaPulse.pause();
      else this.ctaPulse.resume();
    }
    if (on && this.primaryCta?.active) this.primaryCta.setScale(1);
  }

  /**
   * WIN mascot: the unified raster muncher (board identity), celebrating — an entry chomp bite,
   * then the idle loop + bob. Geometric fallback only when the sheet failed to decode. The equipped
   * cosmetic tint carries over so the character on the podium is *your* character.
   */
  victoryMascot(cx: number, y: number, size: number): void {
    const mas = attachMascot(this, 'player', cx, y, size, { skin: selectedCosmetic(), depth: 4 });
    if (mas.raster && motionAllowed() && this.anims.exists('mun-player-chomp')) {
      const spr = mas.obj as Phaser.GameObjects.Sprite;
      spr.play('mun-player-chomp');
      spr.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        if (spr.active && this.anims.exists('mun-player-idle')) spr.play('mun-player-idle');
      });
    }
  }

  /**
   * LOSS mascot: the SAME raster muncher, defeated — tint dimmed to steel, drooped angle, a slow
   * sag and a periodic blink/slump squash, and NO happy chomping idle (frame held). Reduced-motion
   * shows the static dimmed droop. The pose (not hue) carries "defeat" for colorblind players.
   * (No playerHurt raster sheet exists — this is the code-treatment equivalent.)
   */
  defeatedMascot(cx: number, y: number, size: number): void {
    const mas = attachMascot(this, 'player', cx, y, size, { depth: 4, animate: false, bob: false, bloom: false });
    mas.obj.setAngle(-9);
    if (mas.raster) {
      const spr = mas.obj as Phaser.GameObjects.Sprite;
      spr.setTint(0x77808f); // dimmed steel over the cyan body — visibly deflated, still him
      spr.setFrame(0);
    } else {
      mas.obj.setAlpha(0.8);
    }
    if (!motionAllowed()) return;
    // Slow defeated sag (droop deeper and back) + an occasional blink/slump squash. Gentle, low-Hz
    // motion only — nothing here approaches a flash (WCAG 2.3.1 safe).
    this.tweens.add({ targets: mas.obj, angle: -14, y: y + size * 0.05, duration: 2400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    const baseSX = mas.obj.scaleX;
    const baseSY = mas.obj.scaleY;
    this.tweens.add({
      targets: mas.obj,
      scaleX: baseSX * 1.04,
      scaleY: baseSY * 0.92,
      duration: 170,
      yoyo: true,
      repeat: -1,
      repeatDelay: 2100,
      delay: 900,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * The nearest concrete come-back goal, in priority order:
   *   1) the next mastery tier for the topic just played (mastery.ts — thresholds are module-private,
   *      so we probe the pure `topicMastery(topic, state)` resolver with injected counts),
   *   2) the closest cosmetic skin unlock (P2.5 milestone lanes — facts/wins/streak/gauntlets),
   *   3) the next daily-streak day.
   * Returns null only if a goal genuinely can't be framed (never in practice — streak always can).
   */
  nextUnlockGoal(): { icon: IconName; label: string; remaining: string; frac: number } | null {
    const topic = this.args.topic;
    if (topic) {
      const m = topicMastery(topic);
      const probe = (n: number): number => topicMastery(topic, { topicCorrect: { [topic]: n }, facts: [] }).level;
      if (probe(m.correct + 1000) > m.level) {
        // Not at the top tier: walk to the next threshold + back to this tier's floor (bounded — the
        // top tier enters at 120 correct).
        let nextAt = m.correct + 1;
        while (probe(nextAt) === m.level) nextAt += 1;
        let floorAt = 0;
        for (let n = 0; n <= m.correct; n += 1) {
          if (probe(n) === m.level) {
            floorAt = n;
            break;
          }
        }
        const span = Math.max(1, nextAt - floorAt);
        const nextName = topicMastery(topic, { topicCorrect: { [topic]: nextAt }, facts: [] }).label;
        const toGo = nextAt - m.correct;
        return {
          icon: 'star',
          // #52 — word-boundary shorten + the buildNextUnlock shrink-to-fit backstop, so the
          // topic never mid-word ellipsis-truncates ("Countries in Eu…"). ` · ` is the standard connective.
          label: `${nextName} · ${shortenAtWord(this.args.topicLabel || topic, 32)}`,
          remaining: `${toGo} to go`,
          frac: (m.correct - floorAt) / span,
        };
      }
    }
    const cos = nextCosmeticGoal();
    if (cos) {
      return {
        icon: 'palette',
        label: `${cos.cosmetic.label} skin`,
        remaining: cos.progress.remaining,
        frac: cos.progress.frac,
      };
    }
    const stats = getStats();
    const streak = liveDailyStreak(stats);
    const nextDay = streak + 1;
    return {
      icon: 'flame',
      label: `Daily streak day ${nextDay}`,
      remaining: dailyDoneToday(stats) ? 'back tomorrow' : 'play the Daily',
      frac: Math.min(1, streak / nextDay),
    };
  }

  /** One compact, phone-legible NEXT UNLOCK row: icon + kicker + goal + progress bar + count. */
  buildNextUnlock(cx: number, W: number, y: number, somber: boolean): void {
    const goal = this.nextUnlockGoal();
    if (!goal) return;
    const w = Math.min(W * 0.92, 470);
    const tallPortrait = W < this.scale.height && this.scale.height >= 1400;
    const h = tallPortrait ? 96 : 46;
    // Card token (S3 item 3): same cardPanel treatment as the tip card + rank ribbon.
    cardPanel(this, cx, y, w, h, {
      fill: PALETTE.surface,
      border: somber ? PALETTE.line : PALETTE.lineStrong,
      accent: PALETTE.scan,
    }).setDepth(4);
    const left = cx - w / 2 + 12;
    const top = y - h / 2;
    const kickerY = tallPortrait ? top + 20 : y - 13;
    const labelY = tallPortrait ? top + 42 : y + 1;
    const progressY = tallPortrait ? y + 28 : y + 13;
    icon(this, goal.icon, left + 8, tallPortrait ? top + 31 : y - 6, 16, PALETTE.scan).setDepth(5);
    labelText(this, left + 24, kickerY, 'NEXT UNLOCK', { size: 9, color: PALETTE.dim }).setDepth(5);
    const remain = this.add
      .text(cx + w / 2 - 12, labelY, goal.remaining, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(11, 10)}px`,
        color: hex(PALETTE.scan),
        fontStyle: '600',
      })
      .setOrigin(1, 0.5)
      .setDepth(5);
    const label = this.add
      .text(left + 24, labelY, goal.label, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);
    // Keep the goal text clear of the right-aligned count on narrow phones.
    const maxLabelW = cx + w / 2 - 12 - remain.width - 8 - (left + 24);
    if (maxLabelW > 0 && label.width > maxLabelW) label.setScale(maxLabelW / label.width);
    if (tallPortrait) {
      this.add
        .text(left + 24, top + 62, 'Board progress carries forward', {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(11, 10)}px`,
          color: hex(PALETTE.dim),
          fontStyle: '600',
        })
        .setOrigin(0, 0.5)
        .setDepth(5);
    }
    progressBar(this, left, progressY, w - 24, tallPortrait ? 7 : 5, goal.frac, PALETTE.scan).container.setDepth(5);
  }

  /** Mobile-only lower-stack filler: a real run recap row so the result dock never floats below a void. */
  buildResultRunRecap(
    cx: number,
    W: number,
    y: number,
    h: number,
    v: { played: number; runTotal: number; peakCombo: number; threaded: number; accuracy: number },
  ): void {
    const w = Math.min(W * 0.92, 470);
    cardPanel(this, cx, y, w, h, { fill: PALETTE.surface, border: PALETTE.line, accent: PALETTE.armed }).setDepth(4);
    const left = cx - w / 2 + 18;
    icon(this, 'target', left + 8, y, 16, PALETTE.armed).setDepth(5);
    labelText(this, left + 30, y - 12, 'RUN RECAP', { size: 9, color: PALETTE.dim }).setDepth(5);
    this.add
      .text(left + 30, y + 9, `Board ${v.played}/${v.runTotal} · ${v.accuracy}% clean · ×${v.peakCombo} combo`, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(12, 11)}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);
    const threadText = `${v.threaded} threaded`;
    this.add
      .text(cx + w / 2 - 18, y, threadText, {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(11, 10)}px`,
        color: hex(PALETTE.armed),
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5)
      .setDepth(5);
  }

  /**
   * #38 graduation payoff: a compact "NOW UNLOCKED" row lighting the three affordances the tutorial
   * teased — Topics, Daily, and the Rival — so finishing training reads as the game opening up, not as
   * another BOARD CLEARED. Renderer-only (no stats/economy touch). Also arms a one-shot spotlight
   * handshake the home hub can read on entry (see the graduation goMenu path).
   */
  buildGraduationUnlocks(cx: number, W: number, y: number): void {
    const w = Math.min(W * 0.92, 470);
    const h = 62;
    cardPanel(this, cx, y, w, h, { fill: PALETTE.surface, border: PALETTE.gold, accent: PALETTE.gold }).setDepth(4);
    labelText(this, cx - w / 2 + 16, y - h / 2 + 12, 'NOW UNLOCKED', { size: 9, color: PALETTE.gold }).setDepth(5);
    const items: Array<{ icon: IconName; label: string }> = [
      { icon: 'target', label: 'TOPICS' },
      { icon: 'flame', label: 'DAILY' },
      { icon: 'sword', label: 'RIVAL' },
    ];
    const slotW = w / items.length;
    const rowY = y + 8;
    items.forEach((it, i) => {
      const sx = cx - w / 2 + slotW * (i + 0.5);
      icon(this, it.icon, sx - 30, rowY, 15, PALETTE.gold).setDepth(5);
      this.add
        .text(sx - 16, rowY, it.label, { fontFamily: FONT.display, fontSize: `${scaledFont(12, 11)}px`, color: hex(PALETTE.text), fontStyle: 'bold' })
        .setOrigin(0, 0.5)
        .setDepth(5);
    });
  }

  /**
   * Desktop Tactical OLED treatment for the Result surface. Mobile keeps the original thumb-zone stack;
   * wide landscape gets two quiet diagnostic rails so the payoff reads as a full-screen command surface
   * instead of a phone result column floating in empty canvas. Presentation-only: every value is derived
   * from the final snapshot / Result args already being rendered in the center column.
   */
  buildDesktopResultRails(v: {
    cx: number;
    W: number;
    H: number;
    statW: number;
    screen: 'win' | 'loss' | 'match';
    won: boolean;
    topicLabel: string;
    rule: string;
    boardIndex: number;
    quota: number;
    eatenCorrect: number;
    accuracy: number;
    peakCombo: number;
    threaded: number;
    humanScore: number;
    aiScore: number;
    decoyEaten: number;
    played: number;
    runTotal: number;
    somber: boolean;
  }): void {
    const railGap = Math.max(32, Math.min(46, v.W * 0.034));
    const railW = Math.min(250, (v.W - v.statW) / 2 - railGap * 1.35);
    if (railW < 214) return;
    const railH = Math.min(292, Math.max(248, v.H * 0.36));
    const railY = v.H * 0.46;
    const leftX = v.cx - v.statW / 2 - railGap - railW / 2;
    const rightX = v.cx + v.statW / 2 + railGap + railW / 2;
    if (leftX - railW / 2 < 24 || rightX + railW / 2 > v.W - 24) return;

    const titleSize = scaledFont(12, 11);
    const bodySize = scaledFont(12, 10);
    const valueSize = scaledFont(17, 13);
    const depth = 3;
    const innerLeft = (x: number): number => x - railW / 2 + 18;
    const innerRight = (x: number): number => x + railW / 2 - 18;
    const fit = (t: Phaser.GameObjects.Text, maxW: number, minScale = 0.82): Phaser.GameObjects.Text => {
      if (t.width > maxW) t.setScale(Math.max(minScale, maxW / t.width));
      return t;
    };
    const addText = (
      x: number,
      y: number,
      text: string,
      opts: { size?: number; color?: number; style?: string; originX?: number; maxW?: number; family?: string } = {},
    ): Phaser.GameObjects.Text => {
      const t = this.add
        .text(x, y, text, {
          fontFamily: opts.family ?? FONT.display,
          fontSize: `${opts.size ?? bodySize}px`,
          color: hex(opts.color ?? PALETTE.text),
          fontStyle: opts.style ?? '600',
          wordWrap: opts.maxW ? { width: opts.maxW } : undefined,
        })
        .setOrigin(opts.originX ?? 0, 0.5)
        .setDepth(depth + 1);
      if (opts.maxW) fit(t, opts.maxW);
      return t;
    };
    const ruleText = (v.rule || 'Eat the correct tiles').replace(/\s+/g, ' ').trim();
    const topic = shortenAtWord(v.topicLabel || 'Selected topic', 28);
    const result =
      v.screen === 'match'
        ? 'MATCH SETTLED'
        : v.won
          ? 'BOARD CLEARED'
          : 'RUN FAILED';
    const resultColor = v.somber ? PALETTE.hazard : PALETTE.gold;
    const margin = Math.round(v.humanScore - v.aiScore);
    const signedMargin = `${margin >= 0 ? '+' : ''}${margin.toLocaleString('en-US')}`;

    const drawRail = (x: number, accent: number): void => {
      cardPanel(this, x, railY, railW, railH, {
        fill: PALETTE.surface,
        border: PALETTE.line,
        accent,
        radius: 12,
        borderWeight: 1.1,
      }).setDepth(depth);
    };
    const row = (x: number, y: number, label: string, value: string, color: number = PALETTE.text): void => {
      labelText(this, innerLeft(x), y - 9, label, { size: 9, color: PALETTE.dim }).setDepth(depth + 1);
      fit(
        this.add
          .text(innerLeft(x), y + 10, value, {
            fontFamily: FONT.display,
            fontSize: `${valueSize}px`,
            color: hex(color),
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
          .setDepth(depth + 1),
        railW - 36,
        0.76,
      );
    };

    drawRail(leftX, PALETTE.scan);
    icon(this, 'target', innerLeft(leftX) + 7, railY - railH / 2 + 23, 15, PALETTE.scan).setDepth(depth + 1);
    addText(innerLeft(leftX) + 24, railY - railH / 2 + 23, 'MISSION', { size: titleSize, color: PALETTE.scan, style: 'bold' });
    row(leftX, railY - railH / 2 + 64, 'TOPIC', topic, PALETTE.text);
    labelText(this, innerLeft(leftX), railY - railH / 2 + 110, 'RULE', { size: 9, color: PALETTE.dim }).setDepth(depth + 1);
    this.add
      .text(innerLeft(leftX), railY - railH / 2 + 134, ruleText, {
        fontFamily: FONT.family,
        fontSize: `${bodySize}px`,
        color: hex(PALETTE.textDim),
        fontStyle: '600',
        wordWrap: { width: railW - 36 },
        lineSpacing: 2,
      })
      .setOrigin(0, 0.5)
      .setDepth(depth + 1);
    row(leftX, railY + 36, 'QUOTA', `${Math.min(v.eatenCorrect, v.quota)}/${v.quota}`, v.won ? PALETTE.correct : PALETTE.armed);
    progressBar(this, innerLeft(leftX), railY + 69, railW - 36, 5, Math.min(1, v.eatenCorrect / Math.max(1, v.quota)), v.won ? PALETTE.correct : PALETTE.armed).container.setDepth(depth + 1);
    addText(innerLeft(leftX), railY + railH / 2 - 24, `BOARD ${v.boardIndex + 1}`, {
      size: 11,
      color: PALETTE.dim,
      style: '600',
      maxW: railW - 36,
    });

    drawRail(rightX, v.somber ? PALETTE.hazard : PALETTE.gold);
    icon(this, 'gauge', innerLeft(rightX) + 7, railY - railH / 2 + 23, 15, v.somber ? PALETTE.hazard : PALETTE.gold).setDepth(depth + 1);
    addText(innerLeft(rightX) + 24, railY - railH / 2 + 23, 'COMBAT LOG', {
      size: titleSize,
      color: v.somber ? PALETTE.hazard : PALETTE.gold,
      style: 'bold',
    });
    row(rightX, railY - railH / 2 + 64, 'RESULT', result, resultColor);
    const marginColor = margin >= 0 ? PALETTE.player : PALETTE.rival;
    row(rightX, railY - railH / 2 + 112, 'RIVAL DELTA', signedMargin, marginColor);
    const routeColor = v.threaded > 0 ? PALETTE.armed : v.decoyEaten > 0 ? PALETTE.hazard : PALETTE.textDim;
    row(
      rightX,
      railY - railH / 2 + 160,
      'ROUTE EVENTS',
      `${v.threaded} ${v.threaded === 1 ? 'thread' : 'threads'} · ${v.decoyEaten} ${v.decoyEaten === 1 ? 'decoy' : 'decoys'}`,
      routeColor,
    );
    row(rightX, railY - railH / 2 + 208, 'RUN LEDGER', `${v.played}/${v.runTotal} boards`, PALETTE.scan);
    labelText(this, innerLeft(rightX), railY + railH / 2 - 44, 'NEXT SIGNAL', { size: 9, color: PALETTE.dim }).setDepth(depth + 1);
    addText(innerLeft(rightX), railY + railH / 2 - 24, v.won ? 'ADVANCE ROUTE' : 'RETRY ROUTE', {
      size: 13,
      color: v.won ? PALETTE.correct : PALETTE.hazard,
      style: 'bold',
      maxW: railW - 36,
    });
    this.add
      .rectangle(innerRight(rightX), railY + railH / 2 - 24, 7, 7, v.won ? PALETTE.correct : PALETTE.hazard, 0.95)
      .setRotation(Math.PI / 4)
      .setDepth(depth + 1);
  }

  cancelIdleAdvance(): void {
    if (!this.idleAdvanceTimer) return;
    this.idleAdvanceTimer.remove(false);
    this.idleAdvanceTimer = undefined;
    this.idleAdvanceText?.destroy();
    this.idleAdvanceText = undefined;
  }

  /**
   * STRETCH #16: build the killcam model from the FINAL board's event-log tail and wire the entry play +
   * REPLAY chip. The log is reached read-only from the (now-stopped) Game scene — `sim` survives its
   * SHUTDOWN until the next board boots, so `sim.log` here is the completed final board. PURE consumer:
   * board LAYOUT comes from the passed snapshot, the ROUTE from the log tail, so byte-identical logs
   * render byte-identical replays. Silently no-ops (no chip, no overlay) when ineligible or the tail is
   * too thin — the still share card is never affected.
   */
  setupKillcam(
    snap: Snapshot,
    v: { won: boolean; perfectWin: boolean; photoFinish: boolean; photoMargin: number; topicLabel: string },
  ): void {
    // Never on warmup / novice (no competitive stake); otherwise a rival board, a perfect run, or a
    // ladder duel all qualify (photo-finish is a subset of these).
    if (this.args.rivalWarmup || this.args.noviceRun) return;
    const eligible = this.args.ai || v.perfectWin || this.args.ladderGhost != null;
    if (!eligible) return;

    const gameScene = this.scene.get('Game') as GameScene | undefined;
    const events = gameScene?.sim?.log?.all();
    const model = buildKillcamModel(events, snap, {
      won: v.won,
      photoFinish: v.photoFinish,
      perfect: v.perfectWin,
      margin: Number.isFinite(v.photoMargin) ? v.photoMargin : undefined,
      label: v.topicLabel,
    });
    this.killcamModel = model;
    if (!model) return;

    // REPLAY chip — top-left (REPORT owns top-right). A caret glyph + a legible label with a ≥44px tap
    // target; opens the killcam overlay on tap. Non-blocking; sits above the card at depth 6.
    const chipY = this.cameras.main.height * 0.05;
    const glyph = icon(this, 'caretRight', 26, chipY, 15, PALETTE.scan).setDepth(6);
    const label = this.add
      .text(40, chipY, 'REPLAY', { fontFamily: FONT.display, fontSize: `${scaledFont(12, 11)}px`, color: hex(PALETTE.scan), fontStyle: '600' })
      .setOrigin(0, 0.5)
      .setDepth(6);
    const hitW = 40 + label.width + 12;
    const hit = this.add
      .rectangle(hitW / 2, chipY, hitW, 44, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true })
      .setDepth(6);
    const paint = (c: number): void => {
      label.setColor(hex(c));
      glyph.clear();
      drawIcon(glyph, 'caretRight', 15, c);
    };
    hit.on('pointerover', () => paint(PALETTE.text));
    hit.on('pointerout', () => paint(PALETTE.scan));
    hit.on('pointerup', () => this.playKillcamOverlay(model));

    // Keep Result pacing player-led: the route replay is available from the REPLAY chip and share flow,
    // but it no longer auto-opens over the primary NEXT BOARD action on entry.
  }

  /**
   * Share cards are static and spoiler-safe, but the final snapshot alone can forget the player's route:
   * Core clears `armed` decoys after 0.6s, so a result reached a beat later can render an all-blank path
   * even though the player threaded decoys during the run. The completed GameScene event log is still
   * available while Result is open; use human `passArm` events to restore only the decoys the player
   * routed through. Human eats still come from the final snapshot, and untouched correct/decoy cells stay
   * indistinguishable.
   */
  buildShareRouteCells(snap: Snapshot): ShareRouteCells {
    const cells: ShareRouteCells = snap.cells.map((c) => ({
      kind: c.kind,
      eaten: c.eaten,
      eatenBy: c.eatenBy,
      armed: c.armed,
    }));
    const gameScene = this.scene.get('Game') as GameScene | undefined;
    const events = gameScene?.sim?.log?.all();
    if (!events || events.length === 0) return cells;

    const indexById = new Map<number, number>();
    snap.cells.forEach((cell, index) => indexById.set(cell.id, index));
    const threadedDecoys = new Set<number>();
    for (const e of events) {
      if (e.player !== 0 || e.cell == null || e.type !== 'passArm') continue;
      const index = indexById.get(e.cell);
      const cell = index === undefined ? undefined : snap.cells[index];
      if (index !== undefined && cell?.kind === 'decoy') threadedDecoys.add(index);
    }
    if (threadedDecoys.size === 0) return cells;

    return cells.map((cell, index) => {
      if (cell.kind !== 'decoy' || !threadedDecoys.has(index)) return cell;
      // The human's explicit decoy eat remains red; otherwise restore the threaded-through amber state.
      if (cell.eaten && cell.eatenBy === 0) return cell;
      return { ...cell, armed: true };
    });
  }

  /** Open the killcam as a scrimmed overlay; `modalOpen` blocks the result's tap/keyboard advance while
   *  it plays (the overlay owns its own tap-to-skip). reduced-motion shows the static route-trace frame. */
  playKillcamOverlay(model: KillcamModel): void {
    if (this.modalOpen) return;
    this.modalOpen = true;
    // S9 FIX-D: the overlay owns the screen — the Result's own caption/CTA glow yields until it closes.
    this.setKillcamCaptionSuppressed(true);
    playKillcam(this, model, {
      reducedMotion: !motionAllowed(),
      onSkip: () => {
        this.skipScoreTally();
      },
      onDone: () => {
        this.modalOpen = false;
        this.setKillcamCaptionSuppressed(false);
      },
    });
  }

  /** Animate a score number from 0 up to its final value (juice). Static under reduced motion. */
  countUp(t: Phaser.GameObjects.Text, to: number): void {
    const fmt = (n: number): string => Math.round(n).toLocaleString('en-US');
    if (!motionAllowed() || to <= 0) {
      t.setText(fmt(to));
      return;
    }
    this.scoreTallyComplete = false;
    this.scoreTallyItems.push({ text: t, finalValue: to });
    const o = { v: 0 };
    const tween = this.tweens.add({
      targets: o,
      v: to,
      duration: 430,
      ease: 'Cubic.easeOut',
      onUpdate: () => t.setText(fmt(o.v)),
      onComplete: () => {
        t.setText(fmt(to));
        this.scoreTallyTweens = this.scoreTallyTweens.filter((item) => item !== tween);
        this.updateScoreTallyComplete();
      },
    });
    this.scoreTallyTweens.push(tween);
  }

  /**
   * Tap-anywhere on the result surface (Gemini P1 — the screen used to linger with no prompt). First
   * tap lands the still-animating tally; the next tap runs the primary CTA (next board / new match).
   * Two guards keep it from swallowing a real control tap:
   *   1) the bottom trio band (MENU / SHARE / RANKS) is excluded outright, so a near-miss there never
   *      advances instead of sharing / opening ranks;
   *   2) any tap that hit-tests onto an interactive object (the primary CTA, the REPORT link, the
   *      COPY TEST ROW) is left to that object — so tap-anywhere never double-fires or steals its tap.
   */
  handleResultTap(pointer?: Phaser.Input.Pointer): void {
    if (this.modalOpen || !pointer) return;
    if (pointer.y >= this.tapGuardY) return;
    if (this.input.hitTestPointer(pointer).length > 0) return;
    this.cancelIdleAdvance();
    if (this.skipScoreTally()) return;
    this.runPrimary?.();
  }

  /** Keep the skip hint only while the tally animates; the landed state relies on the primary CTA. */
  refreshTallyHint(): void {
    const h = this.tallyHint;
    if (!h?.active) return;
    if (this.scoreTallyComplete) {
      h.destroy();
      this.tallyHint = undefined;
      return;
    }
    h.setText('TAP TO SKIP');
  }

  skipScoreTally(): boolean {
    if (this.scoreTallyComplete) return false;
    for (const tween of this.scoreTallyTweens) tween.stop();
    this.scoreTallyTweens = [];
    for (const item of this.scoreTallyItems) item.text.setText(String(item.finalValue));
    this.scoreTallyComplete = true;
    this.refreshTallyHint();
    return true;
  }

  updateScoreTallyComplete(): void {
    if (this.scoreTallyTweens.length === 0) {
      this.scoreTallyComplete = true;
      this.refreshTallyHint();
    }
  }

  /**
   * A friendly retention ribbon, or '' if nothing special happened. PROGRESSION rides this same
   * single line (no new block): a rank-up takes priority, then first-session unlocks, then a single
   * completed mission, then the existing best/streak beats. We surface at most one phrase so the
   * ribbon stays one tight line on a 360px card.
   */
  achievementText(rec: RecordResult | null): string {
    if (!rec) return '';
    // Highest-priority beats first (a single line, so we return the top one that applies).
    // A ▲ prefix double-codes RANK UP for colorblind users (it's the one ribbon tinted the YOU/player
    // hue, so a non-color glyph carries the same "you leveled up" signal); ✓ marks a completed mission.
    if (rec.rankedUp) return `▲ RANK UP → ${rec.rankName}!`;
    if (rec.firstsUnlocked.length) return firstLabel(rec.firstsUnlocked[0]);
    if (rec.missionsCompleted.length) return `✓ MISSION DONE: ${rec.missionsCompleted[0].label}!`;
    const bits: string[] = [];
    if (rec.daily === 'extended' || rec.daily === 'started') {
      bits.push(`DAILY STREAK ${rec.stats.dailyStreak}!`);
    }
    if (rec.newBestScore) bits.push('NEW BEST SCORE!');
    else if (rec.newBestCombo) bits.push('NEW BEST COMBO!');
    if (!bits.length && rec.stats.winStreak >= 3) bits.push(`${rec.stats.winStreak} WINS IN A ROW`);
    return bits.join('   ');
  }

  /**
   * One short result-side sting for the headline meta beat, reusing the existing 28-file SFX set (no
   * new audio). RANK UP rings a bright "perfect" (pitched up a touch); a mission completed this board
   * gets a softer "combo2". Delayed ~520ms so it lands after the win/loss sting instead of stacking,
   * and only ONE fires (rank-up wins) so multiple beats never pile up harshly.
   */
  playMetaSting(rec: RecordResult | null): void {
    if (!rec) return;
    let play: (() => void) | null = null;
    if (rec.rankedUp) {
      play = () => audio().playSfx('perfect', { rate: 1.1, volume: 0.85 });
    } else if (rec.missionsCompleted.length > 0) {
      play = () => audio().playSfx('combo2', { volume: 0.8 });
    }
    if (!play) return;
    this.time.delayedCall(520, () => {
      if (this.scene.isActive()) play!();
    });
  }

  /**
   * Best-effort count of Gauntlets threaded this board, derived purely from the final Snapshot (the
   * event log isn't available here and Core/GameScene are frozen). A decoy the human routed through
   * ends the board `armed` (the same "free Gauntlet pass" signal share.ts uses); a thread needs
   * `gauntletArmThreshold` such traversals before a correct eat, so armedDecoys / threshold is a
   * sound lower bound on threads. Capped by the correct answers the human actually ate.
   */
  threadedFromSnapshot(snap: Snapshot): number {
    let armedDecoys = 0;
    for (const c of snap.cells) {
      if (c.kind === 'decoy' && c.armed) armedDecoys += 1;
    }
    const human = snap.players.find((p) => p.id === 0);
    const eatenCorrect = human?.eatenCorrect ?? 0;
    return Math.min(Math.floor(armedDecoys / SCORING.gauntletArmThreshold), eatenCorrect);
  }

  /**
   * The single forward-looking coach tip (point 2 collapsed the old 3-row Why/Next/Progress panel to
   * just this one line). Returns plain advice text; the result card renders it in neutral grey per the
   * locked color contract, so no signal color is returned.
   */
  resultCoachLines(input: {
    won: boolean;
    eatenCorrect: number;
    quota: number;
    lives: number;
    timeLeftMs: number;
    humanScore: number;
    aiScore: number;
    peakCombo: number;
  }): { next: string } {
    const missing = Math.max(0, input.quota - input.eatenCorrect);
    let next = 'Thread safe passes to protect combo';
    if (this.args.rivalWarmup) {
      next = 'Rival joins now; keep using SCAN';
    } else if (!input.won) {
      if (missing >= 3) next = 'SCAN clusters before guessing';
      else if (this.args.ai) next = 'Try Practice, then race the rival';
      else next = 'Move off decoys; eat sure tiles';
    } else if (input.peakCombo < 3) {
      next = 'Build a 3x combo for the next board';
    }
    return { next };
  }

  analyticsDims(extra: AnalyticsDims = {}): AnalyticsDims {
    return {
      boardSource: this.args.boardSource ?? 'unknown',
      topicCategory: this.args.topic,
      difficulty: this.args.boardIndex + 1,
      boardIndex: this.args.boardIndex,
      ...extra,
    };
  }

  /**
   * P1.2 challenge payoff banner: only when this board was launched from a friend's challenge link
   * (?target=N). Sits above the learning card so the "did I beat them?" verdict reads instantly.
   */
  challengeVerdictHeight(): number {
    const series = this.args.series ?? null;
    const target = Math.max(0, Math.floor(this.args.challengeTarget ?? 0));
    if (target <= 0 && !series) return 0;
    const twoLine = !!series;
    return Math.max(twoLine ? 58 : 44, Math.min(twoLine ? 78 : 60, this.scale.height * (twoLine ? 0.084 : 0.06)));
  }

  buildChallengeVerdict(cx: number, W: number, y: number, humanScore: number): number {
    const series = this.args.series ?? null;
    const target = Math.max(0, Math.floor(this.args.challengeTarget ?? 0));
    if (target <= 0 && !series) return 0;
    const w = Math.min(W * 0.86, 460);
    const h = this.challengeVerdictHeight();
    let accent: number = PALETTE.armed;
    let line1: string;
    let line2 = '';
    if (series) {
      // P1.2c best-of-three. RESOLVE phase: a target was carried, so score this game + advance the
      // standings. SET phase (no target): the player just played a board to send as the next game.
      if (target > 0) {
        const won = humanScore >= target;
        const diff = Math.abs(humanScore - target);
        const advanced = advanceSeries(series, won);
        line1 = won
          ? `Game ${series.game}: out-ran their route (+${diff})`
          : `Game ${series.game}: ${diff} short of their route`;
        if (isSeriesOver(advanced)) {
          accent = seriesWonByYou(advanced) ? PALETTE.correct : PALETTE.hazard;
          line2 = seriesStandingText(advanced);
        } else {
          accent = won ? PALETTE.correct : PALETTE.hazard;
          line2 = `${seriesStandingText(advanced)} — tap NEXT GAME`;
        }
      } else {
        accent = PALETTE.scan;
        line1 = `You scored ${humanScore} — ${seriesGameLabel(series)}`;
        line2 = `${seriesStandingText(series)} — tap SEND NEXT`;
      }
    } else {
      const beat = humanScore >= target;
      const diff = Math.abs(humanScore - target);
      accent = beat ? PALETTE.correct : PALETTE.armed;
      line1 = beat ? `You beat their route by ${diff}!` : `${diff} short of their route — run it back!`;
    }
    this.add.rectangle(cx, y, w, h, PALETTE.panel, 0.94).setStrokeStyle(2, accent, 0.95).setDepth(9);
    this.add
      .text(cx, y, line2 ? `${line1}\n${line2}` : line1, {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'body', { vw: 0.026, min: 13 })}px`,
        color: hex(accent),
        fontStyle: 'bold',
        align: 'center',
        lineSpacing: 2,
        wordWrap: { width: w - 24 },
      })
      .setOrigin(0.5)
      .setDepth(10);
    return h;
  }

  /**
   * GHOST DUELS Slice C: score a ladder duel and celebrate it. Out-run the challenger's ghost score →
   * a green "GHOST DOWN" banner + the rung persists as beaten (drives the panel check); fall short → an
   * amber "N short" nudge. Returns true iff a banner was drawn (a ladder run) so the caller can cede the
   * NEXT-UNLOCK band. Names are capped 12 chars (privacy). No-op (returns false) on a non-ladder run.
   */
  buildLadderVerdict(cx: number, W: number, y: number, humanScore: number): boolean {
    const ladder = this.args.ladderGhost;
    if (!ladder) return false;
    const name = (ladder.record.name || 'GHOST').slice(0, 12);
    const ghostScore = Math.max(0, Math.floor(ladder.record.score));
    // Score the rung on MUNCH-SUM vs MUNCH-SUM: record.score is the ghost's cumulative munch value (what its
    // racing chip shows), so compare the human's OWN munch-sum (humanTrackScore) — NOT the displayed full sim
    // score, which carries perfectBoardBonus/surge the ghost's track structurally can't, and would fire a
    // phantom "GHOST DOWN". Defensive fallback to humanScore only if the sum wasn't threaded (never on the
    // real ladder path). This basis also drives the VO trigger below, so the bark matches the true outcome.
    const humanMunch = this.args.humanTrackScore ?? humanScore;
    const beat = humanMunch > ghostScore;
    const diff = Math.abs(humanMunch - ghostScore);
    // Slice D gatekeeper VO: BYT3 reacts to the ghost duel. Out-running rung 3 (the day's #1 ghost) is
    // the ladder CLEAR — a distinct stunned line — otherwise a generic "ghost down"; a shortfall gets the
    // mock. 1900ms clears the 1600ms VO-yield window after voYouWin (ledgered knife-edge, matches the
    // board-end bark above). The generic board bark is already suppressed for ladder runs, so this owns it.
    if (beat) {
      markRungBeaten(ladder.rung);
      resultSceneBark(this, ladder.rung >= 3 ? 'barkLadderClear' : 'barkRungWin', 1900);
    } else {
      resultSceneBark(this, 'barkRungLoss', 1900);
    }
    const accent = beat ? PALETTE.correct : PALETTE.armed;
    const line = beat
      ? `GHOST DOWN — out-ran ${name} by +${diff}!`
      : diff === 0
        ? `Dead heat with ${name}'s ghost — edge it next run!`
        : `${diff} short of ${name}'s ghost — run it back!`;
    const w = Math.min(W * 0.86, 460);
    const h = Math.max(44, Math.min(60, this.scale.height * 0.06));
    this.add.rectangle(cx, y, w, h, PALETTE.panel, 0.94).setStrokeStyle(2, accent, 0.95).setDepth(9);
    this.add
      .text(cx, y, line, {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'body', { vw: 0.026, min: 13 })}px`,
        color: hex(accent),
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: w - 24 },
      })
      .setOrigin(0.5)
      .setDepth(10);
    return true;
  }

  /**
   * P1.3 mastery recording: credit every correct answer the player CHOMPed toward the per-topic
   * mastery signal + the dedup'd "facts learned" collection. Returns the count of NEW facts (so the
   * result can celebrate them). Called once per non-training board in the guarded recording block.
   */
  recordMastery(snap: Snapshot): number {
    const topic = this.args.topic;
    if (!topic) return 0;
    let correctEaten = 0;
    let learned = 0;
    for (const c of snap.cells) {
      if (c.eatenBy === 0 && c.kind === 'correct') {
        correctEaten += 1;
        const fact = (c.explanation || '').trim();
        if (fact && recordFactLearned(topic, c.text, fact)) learned += 1;
      }
    }
    if (correctEaten > 0) recordTopicCorrect(topic, correctEaten);
    return learned;
  }

  // --- Report controller (extracted; see game/resultReport.ts) ---
  onShare = Report.onShare;
  openFeelReviewDraftModal = Report.openFeelReviewDraftModal;
  openReportModal = Report.openReportModal;
  reportPayload = Report.reportPayload;
  toast = Report.toast;


  /**
   * P1.2c: the primary-CTA label while a best-of-three series is live, or null when there is no series
   * (the normal CONTINUE/START RACE/OPEN HOME logic then applies). RESOLVE phase (the friend's target
   * was carried) reads NEXT GAME, or FINISH once this game decides the series; a SET phase (no target —
   * the player just played a board to send) reads DONE (they tap CHALLENGE to send it).
   */
  seriesPrimaryLabel(): string | null {
    const series = this.args.series;
    if (!series) return null;
    const target = Math.max(0, Math.floor(this.args.challengeTarget ?? 0));
    if (target <= 0) return 'SEND NEXT'; // SET phase: the primary CTA shares this board to the friend.
    const human = this.args.snapshot.players.find((p) => p.id === 0);
    const advanced = advanceSeries(series, (human?.score ?? 0) >= target);
    return isSeriesOver(advanced) ? 'FINISH' : 'NEXT GAME';
  }

  /**
   * P1.2c: drive the series forward from the primary CTA. Returns true once it has handled navigation.
   * RESOLVE phase: score the game vs the friend's target, advance the standings, then either end the
   * series (back to menu) or SET the next game — relaunch the SAME topic via the menu's deep-link path
   * with the advanced standings written to the registry, a fresh per-game seed, and no target (the
   * player is setting it). SET phase: nothing to resolve, so CONTINUE just returns to the menu (the
   * player sends the board they just set via the CHALLENGE button).
   */
  advanceSeriesFlow(): boolean {
    const series = this.args.series;
    if (!series) return false;
    const target = Math.max(0, Math.floor(this.args.challengeTarget ?? 0));
    const toMenu = (): void => {
      resetRun(this);
      this.scene.start('Menu');
      this.scene.stop('Result');
    };
    if (target <= 0) {
      toMenu();
      return true;
    }
    const human = this.args.snapshot.players.find((p) => p.id === 0);
    const advanced = advanceSeries(series, (human?.score ?? 0) >= target);
    if (isSeriesOver(advanced)) {
      toMenu();
      return true;
    }
    // SET the next game on the same topic: advanced standings + a fresh board (distinct seed) + no target.
    this.registry.set(REG.series, advanced.mode);
    this.registry.set(REG.seriesGame, advanced.game);
    this.registry.set(REG.seriesYou, advanced.youWins);
    this.registry.set(REG.seriesThem, advanced.themWins);
    this.registry.set(REG.seed, seriesSeed(this.args.seed, advanced.game));
    this.registry.set(REG.challengeTarget, 0);
    // Clear the friend's prior-game ghost — the SET game is a fresh board they never played, so a
    // stale ghost would paint their old footprints at mismatched cell ids (#4 fix).
    this.registry.set(REG.challengeGhost, '');
    resetRun(this);
    this.scene.start('Menu', { deepLinkTopic: this.args.topicLabel || this.args.topic });
    this.scene.stop('Result');
    return true;
  }

  next(): void {
    this.cancelIdleAdvance();
    // P1.2c best-of-three overrides the normal board rotation while a series is live.
    if (this.args.series && this.advanceSeriesFlow()) return;
    // NEW MATCH (the primary CTA once the 5-board match is decided): a genuinely fresh match — clean
    // run ledger, rung 0 — not a mid-run rung advance that would replay board 4 with stale standings.
    if (this.matchComplete && !this.args.noviceRun && !this.args.rivalWarmup) {
      resetRun(this);
      const curTopicIdx = SEED_TOPICS.findIndex((t) => t.normalized === this.args.topic);
      const fresh = SEED_TOPICS[(curTopicIdx + 1 + SEED_TOPICS.length) % SEED_TOPICS.length];
      this.registry.set(REG.topic, fresh.normalized);
      this.registry.set(REG.boardIndex, 0);
      this.registry.set(REG.daily, false);
      this.scene.start('Game', {
        topic: fresh.normalized,
        topicLabel: fresh.raw,
        seed: this.args.seed,
        ai: this.args.ai,
        rivalWarmup: false,
        boardIndex: 0,
        isDaily: false,
        boardSource: 'seed',
      });
      this.scene.stop('Result');
      return;
    }
    if (this.args.noviceRun) {
      const nextIndex = this.args.boardIndex + 1;
      if (nextIndex >= NOVICE_BOARD_COUNT) {
        resetRun(this);
        this.registry.set(REG.ai, false);
        this.registry.set(REG.daily, false);
        this.registry.set(REG.boardIndex, 0);
        this.scene.start('Menu');
        this.scene.stop('Result');
        return;
      }
      const content = noviceBoardFor(nextIndex);
      const nextAi = noviceAiEnabled(nextIndex);
      this.registry.set(REG.topic, content.topicNormalized);
      this.registry.set(REG.ai, nextAi);
      this.registry.set(REG.boardIndex, nextIndex);
      this.registry.set(REG.daily, false);
      this.scene.start('Game', {
        topic: content.topicNormalized,
        topicLabel: content.topicRaw,
        content,
        seed: this.args.seed,
        ai: nextAi,
        rivalWarmup: false,
        boardIndex: nextIndex,
        isDaily: false,
        boardSource: 'seed',
        noviceRun: true,
      });
      this.scene.stop('Result');
      return;
    }
    // Advance one difficulty rung; rotate to the next seed topic so NEXT feels fresh.
    const nextIndex = Math.min(4, this.args.boardIndex + 1);
    const curTopicIdx = SEED_TOPICS.findIndex((t) => t.normalized === this.args.topic);
    const nextTopic = SEED_TOPICS[(curTopicIdx + 1 + SEED_TOPICS.length) % SEED_TOPICS.length].normalized;
    const nextLabel = SEED_TOPICS[(curTopicIdx + 1 + SEED_TOPICS.length) % SEED_TOPICS.length].raw;
    const nextAi = this.args.rivalWarmup ? true : this.args.ai;
    this.registry.set(REG.topic, nextTopic);
    this.registry.set(REG.ai, nextAi);
    this.registry.set(REG.boardIndex, nextIndex);
    // NEXT keeps the run going but leaves the "daily" framing behind (a rotated seed board isn't the
    // daily) so the daily streak isn't credited twice from one launch.
    this.registry.set(REG.daily, false);
    this.scene.start('Game', {
      topic: nextTopic,
      topicLabel: nextLabel,
      seed: this.args.seed,
      ai: nextAi,
      rivalWarmup: false,
      boardIndex: nextIndex,
      isDaily: false,
    });
    this.scene.stop('Result');
  }
}

/**
 * #52 — shorten to ≤ max chars by breaking on the last word boundary, with NO ellipsis glyph: a long
 * topic reads as a complete phrase ("2026 Tournament Host") instead of a chopped "…"-tailed one
 * ("2026 Tournament Host…"). The buildNextUnlock shrink-to-fit backstop owns the remaining width, so
 * this only needs to avoid a mid-word cut — never a trailing ellipsis. When the first word alone
 * already exceeds the budget there is no clean boundary: hand the whole phrase to shrink-to-fit rather
 * than hard-chopping mid-word (which would read as a typo, not a truncation).
 */
function shortenAtWord(s: string, max: number): string {
  const t = s.trim();
  if (t.length <= max) return t;
  const sp = t.slice(0, max).lastIndexOf(' ');
  return sp > Math.floor(max * 0.5) ? t.slice(0, sp).trimEnd() : t;
}
